import { useState, useEffect, useRef } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  User, Ticket, Settings, LogOut, Calendar, QrCode, Edit2, Check, X, 
  Image, Download, Loader2, Crown, Camera, Sparkles, ChevronRight,
  MapPin, Phone, Mail, Shield, Clock, Lock, Gift, CreditCard, Infinity as InfinityIcon,
  CheckCircle, Heart, UserCheck, ShieldCheck, Music, Briefcase, Newspaper
} from 'lucide-react';
import { format } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { es } from 'date-fns/locale';
import { toast } from 'sonner';
import { QRCodeSVG } from 'qrcode.react';
import { usePublicAuth } from '../context/PublicAuthContext';
import { useAura } from '../context/AuraContext';
import api, { publicUsersApi, subscriptionApi, publicAuthApi } from '../lib/api';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import CardWallet from '../components/CardWallet';
import MyBravxSection from '../components/MyBravxSection';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Avatar component with Siri-style aura
const AvatarUpload = ({ user, onUpdate, isSubscribed }) => {
  const { auraPreset, setAuraPreset, applyToPage, setApplyToPage, currentColors, AURA_PRESETS } = useAura();
  const [isHovered, setIsHovered] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showAuraSettings, setShowAuraSettings] = useState(false);
  const [preview, setPreview] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [localAuraPreset, setLocalAuraPreset] = useState(user.aura_preset || 'monochrome');
  const fileInputRef = useRef(null);
  
  const currentAura = AURA_PRESETS[localAuraPreset] || AURA_PRESETS.monochrome || { name: 'Monocromático', colors: ['#FFFFFF', '#E0E0E0', '#BDBDBD', '#9E9E9E', '#FFFFFF', '#F5F5F5'], premium: false };
  
  const getInitials = (name) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Tipo de archivo no permitido. Use JPG, PNG, WebP o GIF');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('El archivo es muy grande. Máximo 5MB');
      return;
    }

    setSelectedFile(file);
    
    const reader = new FileReader();
    reader.onloadend = () => {
      setPreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const handleUpload = async () => {
    if (!selectedFile) return;
    
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      
      await api.post('/public-auth/me/avatar', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      onUpdate();
      toast.success('¡Avatar actualizado!');
      setShowModal(false);
      setSelectedFile(null);
      setPreview(null);
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Error al subir la imagen');
    } finally {
      setUploading(false);
    }
  };

  const handleSaveAura = async (preset) => {
    setLocalAuraPreset(preset);
    setAuraPreset(preset); // Update global context
    try {
      await api.put('/public-auth/me', { aura_preset: preset });
      onUpdate();
      toast.success('¡Aura actualizada!');
    } catch (error) {
      console.error('Error saving aura:', error);
    }
  };

  const handleToggleApplyToPage = async () => {
    const newValue = !applyToPage;
    setApplyToPage(newValue); // Update global context
    try {
      await api.put('/public-auth/me', { aura_apply_to_page: newValue });
      toast.success(newValue ? 'Aura aplicada a todas las páginas' : 'Aura solo en avatar');
    } catch (error) {
      console.error('Error saving preference:', error);
    }
  };

  const openModal = () => {
    setShowModal(true);
    setPreview(null);
    setSelectedFile(null);
  };

  const getAvatarUrl = (url) => {
    if (!url) return null;
    // Skip local paths that don't persist in production - use null to show default avatar
    if (url.startsWith('/api/uploads/avatars/')) {
      // These local files don't persist after deploy - return null to show initials
      return null;
    }
    if (url.startsWith('/api/')) {
      return `${API_URL}${url}`;
    }
    // Cloudinary URLs and other external URLs work fine
    return url;
  };

  // Generate Siri-style gradient animation
  const generateSiriGradient = (colors, offset = 0) => {
    if (!colors) return 'transparent';
    const stops = colors.map((color, i) => {
      const angle = (i / colors.length) * 360 + offset;
      return `${color} ${angle}deg`;
    }).join(', ');
    return `conic-gradient(from 0deg, ${stops}, ${colors[0]} 360deg)`;
  };

  return (
    <>
      <motion.div
        className="relative cursor-pointer"
        onHoverStart={() => setIsHovered(true)}
        onHoverEnd={() => setIsHovered(false)}
        onClick={openModal}
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
      >
        {/* Siri-style Animated Aura */}
        {currentAura.colors && (
          <div className="absolute -inset-4 rounded-full">
            {/* Main flowing gradient - Layer 1 */}
            <motion.div
              className="absolute inset-0 rounded-full blur-md"
              style={{
                background: generateSiriGradient(currentAura.colors),
                opacity: 0.6,
              }}
              animate={{ 
                rotate: 360,
                scale: [1, 1.05, 1],
              }}
              transition={{ 
                rotate: { duration: 8, repeat: Infinity, ease: "linear" },
                scale: { duration: 3, repeat: Infinity, ease: "easeInOut" }
              }}
            />
            
            {/* Secondary layer - opposite rotation */}
            <motion.div
              className="absolute inset-1 rounded-full blur-sm"
              style={{
                background: generateSiriGradient([...currentAura.colors].reverse()),
                opacity: 0.4,
              }}
              animate={{ 
                rotate: -360,
                scale: [1.05, 1, 1.05],
              }}
              transition={{ 
                rotate: { duration: 6, repeat: Infinity, ease: "linear" },
                scale: { duration: 2.5, repeat: Infinity, ease: "easeInOut" }
              }}
            />
            
            {/* Inner glow pulse */}
            <motion.div
              className="absolute inset-2 rounded-full"
              style={{
                background: `radial-gradient(circle, ${currentAura.colors[0]}40 0%, transparent 70%)`,
              }}
              animate={{ 
                opacity: [0.3, 0.7, 0.3],
                scale: [0.95, 1.1, 0.95],
              }}
              transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
            />

            {/* Sparkle dots orbiting */}
            {currentAura.colors.slice(0, 4).map((color, i) => (
              <motion.div
                key={i}
                className="absolute w-2 h-2 rounded-full"
                style={{
                  background: color,
                  boxShadow: `0 0 10px ${color}, 0 0 20px ${color}`,
                  top: '50%',
                  left: '50%',
                }}
                animate={{
                  x: [
                    Math.cos((i / 4) * Math.PI * 2) * 70,
                    Math.cos((i / 4) * Math.PI * 2 + Math.PI) * 70,
                    Math.cos((i / 4) * Math.PI * 2) * 70,
                  ],
                  y: [
                    Math.sin((i / 4) * Math.PI * 2) * 70,
                    Math.sin((i / 4) * Math.PI * 2 + Math.PI) * 70,
                    Math.sin((i / 4) * Math.PI * 2) * 70,
                  ],
                  opacity: [0.8, 0.4, 0.8],
                  scale: [1, 1.5, 1],
                }}
                transition={{
                  duration: 4 + i * 0.5,
                  repeat: Infinity,
                  ease: "easeInOut",
                  delay: i * 0.3,
                }}
              />
            ))}
          </div>
        )}

        {/* Main Avatar Container */}
        <div className="relative w-28 h-28 md:w-36 md:h-36 rounded-full overflow-hidden border-4 border-[#27272A] group z-10 bg-[#0A0A0A]">
          {user.profile_picture ? (
            <img 
              src={getAvatarUrl(user.profile_picture)} 
              alt={user.full_name} 
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-[#27272A] to-[#18181B] flex items-center justify-center">
              <span className="text-3xl md:text-4xl font-black text-white/80">
                {getInitials(user.full_name)}
              </span>
            </div>
          )}
          
          {/* Hover overlay */}
          <AnimatePresence>
            {isHovered && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="absolute inset-0 bg-black/60 flex items-center justify-center backdrop-blur-sm"
              >
                <Camera size={28} className="text-white" />
              </motion.div>
            )}
          </AnimatePresence>
        </div>
        
        {/* Edit badge */}
        <motion.div 
          className="absolute -bottom-1 -right-1 w-10 h-10 bg-white rounded-full flex items-center justify-center shadow-lg border-2 border-[#0A0A0A] z-20"
          whileHover={{ scale: 1.1, boxShadow: '0 0 20px rgba(255,255,255,0.5)' }}
          whileTap={{ scale: 0.9 }}
        >
          <Edit2 size={16} className="text-black" />
        </motion.div>

        {/* Aura settings button */}
        <motion.button
          className="absolute -bottom-1 -left-1 w-8 h-8 bg-[#27272A] rounded-full flex items-center justify-center border-2 border-[#0A0A0A] z-20"
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          onClick={(e) => {
            e.stopPropagation();
            setShowAuraSettings(true);
          }}
          title="Personalizar aura"
        >
          <Sparkles size={12} className="text-white" />
        </motion.button>
      </motion.div>

      {/* Apply to page toggle - more spacing */}
      {currentAura.colors && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="mt-8 flex items-center justify-center gap-3"
        >
          <button
            onClick={handleToggleApplyToPage}
            className={`relative w-12 h-6 rounded-full transition-all duration-300 ${
              applyToPage 
                ? 'bg-gradient-to-r' 
                : 'bg-[#27272A]'
            }`}
            style={applyToPage ? {
              background: `linear-gradient(to right, ${currentAura.colors[0]}, ${currentAura.colors[2]})`
            } : {}}
          >
            <motion.div
              className="absolute top-1 w-4 h-4 bg-white rounded-full shadow-md"
              animate={{ left: applyToPage ? '1.75rem' : '0.25rem' }}
              transition={{ type: 'spring', stiffness: 500, damping: 30 }}
            />
          </button>
          <span className="text-xs text-[#A1A1AA]">
            {applyToPage ? 'Aura en toda la página' : 'Solo en avatar'}
          </span>
        </motion.div>
      )}

      {/* Hidden file input */}
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileSelect}
        accept="image/jpeg,image/png,image/webp,image/gif"
        className="hidden"
      />

      {/* Upload Dialog */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="bg-[#121212] border border-[#27272A] max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-black uppercase tracking-tight flex items-center gap-2">
              <Camera size={20} />
              Cambiar Avatar
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Preview Area */}
            <div 
              onClick={() => fileInputRef.current?.click()}
              className="relative cursor-pointer group"
            >
              <div className="w-40 h-40 mx-auto rounded-full overflow-hidden border-4 border-[#27272A] bg-[#1A1A1A] flex items-center justify-center transition-all group-hover:border-white">
                {preview ? (
                  <img src={preview} alt="Preview" className="w-full h-full object-cover" />
                ) : user.profile_picture ? (
                  <img src={getAvatarUrl(user.profile_picture)} alt="Current" className="w-full h-full object-cover opacity-50" />
                ) : (
                  <div className="text-center">
                    <Camera size={32} className="mx-auto mb-2 text-[#52525B]" />
                    <p className="text-xs text-[#52525B]">Clic para seleccionar</p>
                  </div>
                )}
                
                {/* Overlay */}
                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center rounded-full">
                  <p className="text-white text-sm font-medium">Cambiar imagen</p>
                </div>
              </div>
            </div>

            {/* File info */}
            {selectedFile && (
              <motion.div 
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-center"
              >
                <p className="text-sm text-white">{selectedFile.name}</p>
                <p className="text-xs text-[#52525B]">
                  {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                </p>
              </motion.div>
            )}

            {/* Instructions */}
            <div className="text-center text-[#52525B] text-xs">
              <p>Formatos: JPG, PNG, WebP, GIF</p>
              <p>Tamaño máximo: 5MB</p>
            </div>

            {/* Actions */}
            <div className="flex gap-3 justify-end">
              <Button
                variant="ghost"
                onClick={() => {
                  setShowModal(false);
                  setPreview(null);
                  setSelectedFile(null);
                }}
                className="text-[#A1A1AA]"
              >
                Cancelar
              </Button>
              <Button
                onClick={() => fileInputRef.current?.click()}
                variant="outline"
                className="border-[#27272A] hover:border-white"
              >
                Seleccionar
              </Button>
              <Button
                onClick={handleUpload}
                disabled={uploading || !selectedFile}
                className="btn-primary"
              >
                {uploading ? (
                  <>
                    <Loader2 size={16} className="animate-spin mr-2" />
                    Subiendo...
                  </>
                ) : (
                  'Guardar'
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Aura Settings Dialog */}
      <Dialog open={showAuraSettings} onOpenChange={setShowAuraSettings}>
        <DialogContent className="bg-[#121212] border border-[#27272A] max-w-lg max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="text-xl font-black uppercase tracking-tight flex items-center gap-2">
              <Sparkles size={20} />
              Personalizar Aura
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-4 overflow-y-auto flex-1">
            <p className="text-[#A1A1AA] text-sm mb-4">
              Elige el estilo de aura para tu avatar
            </p>
            
            {/* Subscription banner if not subscribed */}
            {!isSubscribed && (
              <div className="mb-6 p-4 bg-gradient-to-r from-yellow-500/10 to-orange-500/10 border border-yellow-500/30 rounded-lg">
                <div className="flex items-center gap-3">
                  <Crown size={24} className="text-yellow-500" />
                  <div>
                    <p className="font-bold text-yellow-500">FULL BRAVX</p>
                    <p className="text-xs text-[#A1A1AA]">Desbloquea todos los auras premium y descuentos exclusivos</p>
                  </div>
                </div>
              </div>
            )}
            
            <div className="grid grid-cols-2 gap-4">
              {Object.keys(AURA_PRESETS).length === 0 ? (
                <div className="col-span-2 text-center py-8 text-[#A1A1AA]">
                  <Loader2 className="animate-spin mx-auto mb-2" size={24} />
                  <p className="text-sm">Cargando auras...</p>
                </div>
              ) : Object.entries(AURA_PRESETS)
                  .sort(([, a], [, b]) => {
                    // Sort: free auras first, then premium
                    if (a?.premium && !b?.premium) return 1;
                    if (!a?.premium && b?.premium) return -1;
                    return 0;
                  })
                  .map(([key, preset]) => {
                if (!preset) return null;
                const isLocked = preset.premium && !isSubscribed;
                
                return (
                  <motion.button
                    key={key}
                    onClick={() => {
                      if (isLocked) {
                        toast.error('Necesitas FULL BRAVX para usar este aura');
                        return;
                      }
                      handleSaveAura(key);
                    }}
                    whileHover={{ scale: isLocked ? 1 : 1.02 }}
                    whileTap={{ scale: isLocked ? 1 : 0.98 }}
                    className={`relative p-4 border rounded-lg transition-all ${
                      localAuraPreset === key 
                        ? 'border-white bg-white/10' 
                        : isLocked
                          ? 'border-[#27272A] opacity-60 cursor-not-allowed'
                          : 'border-[#27272A] hover:border-[#52525B]'
                    }`}
                  >
                    {/* Premium badge */}
                    {preset.premium && (
                      <div className="absolute top-2 left-2 flex items-center gap-1">
                        <Crown size={10} className={isSubscribed ? 'text-yellow-500' : 'text-[#52525B]'} />
                      </div>
                    )}
                    
                    {/* Lock overlay for premium items */}
                    {isLocked && (
                      <div className="absolute inset-0 bg-black/40 rounded-lg flex items-center justify-center z-10">
                        <Lock size={20} className="text-[#52525B]" />
                      </div>
                    )}
                    
                    {/* Mini preview */}
                    <div className="w-16 h-16 mx-auto mb-3 relative">
                      {preset.colors ? (
                        <>
                          <motion.div
                            className="absolute inset-0 rounded-full blur-sm"
                            style={{
                              background: `conic-gradient(${preset.colors.join(', ')})`,
                              opacity: isLocked ? 0.3 : 0.7,
                            }}
                            animate={{ rotate: 360 }}
                            transition={{ duration: 4, repeat: Infinity, ease: "linear" }}
                          />
                          <div className="absolute inset-2 rounded-full bg-[#121212] flex items-center justify-center">
                            <span className="text-xs font-bold text-white/60">
                              {getInitials(user.full_name)}
                            </span>
                          </div>
                        </>
                      ) : (
                        <div className="w-full h-full rounded-full bg-[#1A1A1A] border-2 border-[#27272A] flex items-center justify-center">
                          <span className="text-xs font-bold text-white/60">
                            {getInitials(user.full_name)}
                          </span>
                        </div>
                      )}
                    </div>
                    
                    <p className="text-sm font-medium">{preset.name}</p>
                    
                    {/* Color dots */}
                    {preset.colors && (
                      <div className="flex justify-center gap-1 mt-2">
                        {preset.colors.slice(0, 4).map((color, i) => (
                          <div
                            key={i}
                            className="w-2 h-2 rounded-full"
                            style={{ background: color, opacity: isLocked ? 0.3 : 1 }}
                          />
                        ))}
                      </div>
                    )}
                    
                    {/* Selected indicator */}
                    {localAuraPreset === key && !isLocked && (
                      <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        className="absolute top-2 right-2 w-5 h-5 bg-white rounded-full flex items-center justify-center"
                      >
                        <Check size={12} className="text-black" />
                      </motion.div>
                    )}
                  </motion.button>
                );
              })}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

// Stat card component
const StatCard = ({ icon: Icon, value, label, color = 'white' }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    whileHover={{ y: -4, borderColor: '#fff' }}
    className="bg-[#121212] border border-[#27272A] p-5 transition-all duration-300"
  >
    <div className={`w-10 h-10 rounded-full bg-${color}/10 flex items-center justify-center mb-3`}>
      <Icon size={20} className={`text-${color}`} />
    </div>
    <p className="text-3xl font-black mb-1">{value}</p>
    <p className="text-[#52525B] text-xs uppercase tracking-widest">{label}</p>
  </motion.div>
);

const UserAccountPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, loading, logout, updateProfile, needsProfileCompletion, refreshUser } = usePublicAuth();
  const { applyToPage: applyAuraToPage, currentColors: currentAuraColors } = useAura();
  
  // Helper function to check if ticket is valid (valid or confirmed status)
  const isTicketValid = (ticket) => ['valid', 'confirmed'].includes(ticket?.status);
  
  const [activeTab, setActiveTab] = useState('profile');
  const [tickets, setTickets] = useState([]);
  const [ticketsLoading, setTicketsLoading] = useState(true);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(false);
  const [stats, setStats] = useState(null);
  const [verifyingPayment, setVerifyingPayment] = useState(false);
  
  // Gallery purchases state
  const [galleryPurchases, setGalleryPurchases] = useState([]);
  const [galleryLoading, setGalleryLoading] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);
  
  // Subscription state
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);
  const [cancellingSubscription, setCancellingSubscription] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  
  // My Benefits state
  const [myBenefits, setMyBenefits] = useState([]);
  const [myBenefitsLoading, setMyBenefitsLoading] = useState(false);

  // Following Producers state
  const [followingProducers, setFollowingProducers] = useState([]);
  const [followingLoading, setFollowingLoading] = useState(false);

  // Linked accounts state
  const [linkedAccounts, setLinkedAccounts] = useState([]);
  const [linkedAccountsLoading, setLinkedAccountsLoading] = useState(false);
  const [portalLoginLoading, setPortalLoginLoading] = useState(null);

  // Handle portal auto-login
  const handlePortalLogin = async (account) => {
    // DJ accounts don't need special login - they're part of the user's account
    if (account.type === 'dj') {
      navigate(account.portal_url);
      return;
    }
    
    setPortalLoginLoading(account.type);
    try {
      // Determine the correct portal type for login
      // For producer admins (secondary admins), use 'producer_admin'
      // For validators, use 'validator'
      let portalType = account.type;
      if (account.type === 'producer' && account.role === 'admin') {
        portalType = 'producer_admin';
      }
      
      const response = await publicAuthApi.portalLogin(portalType);
      const { access_token, portal_url } = response.data;
      
      // Store the token based on portal type and set the header for immediate use
      if (account.type === 'admin') {
        // For admin portal, we need to store the token and trigger a page reload
        // The AuthContext will verify the token and load full user data
        localStorage.setItem('token', access_token);
        // Store minimal user data - AuthContext will fetch full data from /auth/me
        const userData = {
          id: response.data.account.id,
          username: response.data.account.username,
          full_name: response.data.account.full_name,
          email: response.data.account.email,
          is_owner: response.data.account.is_owner,
          is_active: true
        };
        localStorage.setItem('user', JSON.stringify(userData));
        console.log('[PortalLogin] Admin token stored, redirecting to:', portal_url);
      } else if (account.type === 'producer') {
        localStorage.setItem('producer_token', access_token);
      } else if (account.type === 'press') {
        localStorage.setItem('press_token', access_token);
        // Also set for the PressAuthContext to pick up
        api.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      } else if (account.type === 'partner') {
        localStorage.setItem('partner_token', access_token);
      } else if (account.type === 'validator') {
        localStorage.setItem('validator_token', access_token);
        // Also store as validatorToken for legacy support
        localStorage.setItem('validatorToken', access_token);
      }
      
      toast.success(`¡Bienvenido a ${account.name}!`);
      
      // Use window.location for a full page reload to ensure context resets
      window.location.href = portal_url;
    } catch (error) {
      console.error('Portal login error:', error);
      toast.error(error.response?.data?.detail || 'Error al acceder al portal');
    } finally {
      setPortalLoginLoading(null);
    }
  };

  // Handle payment callback from Rebill
  useEffect(() => {
    const paymentStatus = searchParams.get('payment');
    const subscriptionId = searchParams.get('subscription_id');
    
    if (paymentStatus === 'success' && user && !verifyingPayment) {
      // Activate subscription (with or without subscription_id)
      verifyAndActivateSubscription(subscriptionId);
    } else if (paymentStatus === 'failed') {
      toast.error('El pago no se pudo completar');
      setSearchParams({});
    } else if (paymentStatus === 'pending') {
      toast.info('Tu pago está siendo procesado');
      setSearchParams({});
    }
  }, [searchParams, user]);

  const verifyAndActivateSubscription = async (subscriptionId) => {
    setVerifyingPayment(true);
    try {
      // Use the subscription_id if provided, otherwise use 'latest' to find most recent pending
      const subId = subscriptionId || 'latest';
      const response = await subscriptionApi.verifyRebillPayment(subId);
      if (response.data.success) {
        toast.success(response.data.message || '¡Suscripción activada exitosamente!');
        await refreshUser();
        fetchSubscriptionData();
        setActiveTab('subscription');
      }
    } catch (error) {
      console.error('Error verifying payment:', error);
      if (error.response?.status === 404) {
        // No pending subscription found, maybe already activated
        toast.info('Verificando estado de tu suscripción...');
        await refreshUser();
        fetchSubscriptionData();
        if (user?.subscription_active) {
          toast.success('¡Tu suscripción está activa!');
          setActiveTab('subscription');
        }
      } else {
        toast.error('Error al verificar el pago. Contacta soporte si el problema persiste.');
      }
    } finally {
      setVerifyingPayment(false);
      setSearchParams({});
    }
  };

  // Redirect if not authenticated
  useEffect(() => {
    if (!loading && !user) {
      navigate('/cuenta');
    }
  }, [loading, user, navigate]);

  // Redirect to complete profile if needed
  useEffect(() => {
    if (!loading && user && needsProfileCompletion) {
      navigate('/completar-perfil');
    }
  }, [loading, user, needsProfileCompletion, navigate]);

  // Fetch data on mount
  useEffect(() => {
    if (user) {
      fetchTickets();
      fetchStats();
      fetchSubscriptionData();
      fetchLinkedAccounts();
    }
  }, [user]);

  // Fetch gallery purchases when tab changes
  useEffect(() => {
    if (user && activeTab === 'gallery') {
      fetchGalleryPurchases();
    }
  }, [user, activeTab]);

  // Fetch my benefits when tab changes
  useEffect(() => {
    if (user && activeTab === 'benefits') {
      fetchMyBenefits();
    }
  }, [user, activeTab]);

  // Fetch following producers when tab changes
  useEffect(() => {
    if (user && activeTab === 'following') {
      fetchFollowingProducers();
    }
  }, [user, activeTab]);

  const fetchStats = async () => {
    try {
      const response = await api.get('/public-auth/me/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchLinkedAccounts = async () => {
    setLinkedAccountsLoading(true);
    try {
      const response = await publicAuthApi.getLinkedAccounts();
      setLinkedAccounts(response.data.linked_accounts || []);
    } catch (error) {
      console.error('Error fetching linked accounts:', error);
    } finally {
      setLinkedAccountsLoading(false);
    }
  };
  
  const fetchSubscriptionData = async () => {
    try {
      const response = await api.get('/subscriptions/my-subscription');
      setSubscriptionData(response.data);
    } catch (error) {
      console.error('Error fetching subscription:', error);
    }
  };
  
  const handleSubscribe = async () => {
    setSubscriptionLoading(true);
    try {
      // Use Rebill subscription link directly
      const response = await subscriptionApi.subscribeWithRebill({
        success_url: `${window.location.origin}/mi-cuenta?payment=success`,
        cancel_url: `${window.location.origin}/mi-cuenta`
      });
      
      if (response.data.success && response.data.checkout_url) {
        // Redirect to Rebill checkout
        window.location.href = response.data.checkout_url;
      } else if (response.data.success) {
        // Trial activated immediately - no redirect needed
        toast.success(response.data.message || '¡Suscripción activada!');
        await refreshUser();
        await fetchSubscriptionData();
      } else {
        toast.error('Error al crear la suscripción');
      }
    } catch (error) {
      console.error('Subscribe error:', error);
      toast.error(error.response?.data?.detail || 'Error al suscribirse');
    } finally {
      setSubscriptionLoading(false);
    }
  };
  
  const handleCancelSubscription = async () => {
    setCancellingSubscription(true);
    try {
      const response = await subscriptionApi.cancelSubscription();
      toast.success(response.data.message || 'Suscripción cancelada');
      setShowCancelModal(false);
      await refreshUser();
      await fetchSubscriptionData();
    } catch (error) {
      console.error('Cancel error:', error);
      toast.error(error.response?.data?.detail || 'Error al cancelar');
    } finally {
      setCancellingSubscription(false);
    }
  };

  const handleReactivateSubscription = async () => {
    try {
      const response = await subscriptionApi.reactivateSubscription();
      toast.success(response.data.message || '¡Suscripción reactivada!');
      await refreshUser();
      await fetchSubscriptionData();
    } catch (error) {
      console.error('Reactivate error:', error);
      toast.error(error.response?.data?.detail || 'Error al reactivar');
    }
  };

  const fetchTickets = async () => {
    setTicketsLoading(true);
    try {
      const response = await publicUsersApi.getMyTickets();
      setTickets(response.data);
    } catch (error) {
      console.error('Error fetching tickets:', error);
    } finally {
      setTicketsLoading(false);
    }
  };

  const fetchGalleryPurchases = async () => {
    setGalleryLoading(true);
    try {
      const response = await api.get('/gallery/my-purchases');
      setGalleryPurchases(response.data);
    } catch (error) {
      console.error('Error fetching gallery purchases:', error);
    } finally {
      setGalleryLoading(false);
    }
  };

  const fetchMyBenefits = async () => {
    setMyBenefitsLoading(true);
    try {
      const response = await api.get('/subscriptions/my-benefits');
      setMyBenefits(response.data);
    } catch (error) {
      console.error('Error fetching my benefits:', error);
    } finally {
      setMyBenefitsLoading(false);
    }
  };

  const handleDownloadPurchase = async (purchase) => {
    if (purchase.status !== 'completed') {
      toast.error('El pago aún está pendiente');
      return;
    }
    
    setDownloadingId(purchase.id);
    try {
      // Use different endpoint and method based on download type
      // Free downloads use POST, premium downloads use GET
      let response;
      if (purchase.download_type === 'free') {
        response = await api.post(`/gallery/free-download/${purchase.file_id}`, {}, {
          responseType: 'blob'
        });
      } else {
        response = await api.get(`/gallery/premium-download/${purchase.id}`, {
          responseType: 'blob'
        });
      }
      
      const url = window.URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `bravo_${purchase.download_type === 'free' ? 'hd' : 'premium'}_${purchase.file_id}.jpg`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success('¡Descarga completada!');
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Error al descargar');
    } finally {
      setDownloadingId(null);
    }
  };

  const handleLogout = async () => {
    await logout();
    toast.success('Sesión cerrada');
    navigate('/');
  };

  const startEdit = () => {
    setEditData({
      full_name: user?.full_name || '',
      phone: user?.phone || '',
      address: user?.address || '',
      city: user?.city || ''
    });
    setEditMode(true);
  };

  const cancelEdit = () => {
    setEditMode(false);
    setEditData({});
  };

  const saveProfile = async () => {
    setSaving(true);
    try {
      await updateProfile(editData);
      toast.success('Perfil actualizado');
      setEditMode(false);
    } catch (error) {
      toast.error('Error al actualizar perfil');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <motion.div 
          animate={{ opacity: [0.5, 1, 0.5] }}
          transition={{ duration: 1.5, repeat: Infinity }}
          className="text-[#A1A1AA]"
        >
          Cargando...
        </motion.div>
      </div>
    );
  }

  if (!user) return null;

  const tabs = [
    { id: 'profile', label: 'Mi Perfil', icon: User },
    { id: 'tickets', label: 'Mis Tickets', icon: Ticket },
    { id: 'bravx', label: 'BRAVX Points', icon: Sparkles },
    { id: 'subscription', label: 'FULL BRAVX', icon: Crown },
    { id: 'benefits', label: 'Mis Beneficios', icon: Gift },
    { id: 'following', label: 'Siguiendo', icon: Heart },
    { id: 'gallery', label: 'Mis Fotos', icon: Image },
  ];

  const fetchFollowingProducers = async () => {
    setFollowingLoading(true);
    try {
      const response = await api.get('/producers/user/following');
      setFollowingProducers(response.data || []);
    } catch (error) {
      console.error('Error fetching following:', error);
      toast.error('Error al cargar productoras');
    } finally {
      setFollowingLoading(false);
    }
  };

  const handleUnfollow = async (producerId) => {
    try {
      await api.delete(`/producers/${producerId}/follow`);
      setFollowingProducers(prev => prev.filter(p => p.id !== producerId));
      toast.success('Dejaste de seguir a esta productora');
    } catch (error) {
      toast.error('Error al dejar de seguir');
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <Navbar />
      
      {/* Payment Verification Overlay */}
      {verifyingPayment && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center">
          <div className="bg-[#121212] border border-[#D4AF37]/30 p-8 rounded-lg text-center max-w-md">
            <Loader2 className="w-12 h-12 animate-spin text-[#D4AF37] mx-auto mb-4" />
            <h3 className="text-xl font-bold text-white mb-2">Activando tu suscripción</h3>
            <p className="text-[#A1A1AA]">Estamos verificando tu pago, por favor espera...</p>
          </div>
        </div>
      )}

      {/* ========== MOBILE LAYOUT - Completely Redesigned ========== */}
      <div className="lg:hidden" data-testid="user-account-page-mobile">
        {/* Mobile Hero Header with Avatar */}
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="relative pt-4 pb-6"
        >
          {/* Ambient Glow Background */}
          <div 
            className="absolute inset-0 opacity-30 pointer-events-none"
            style={{
              background: applyAuraToPage && currentAuraColors 
                ? `radial-gradient(circle at 50% 0%, ${currentAuraColors[0]}40 0%, transparent 60%)`
                : 'radial-gradient(circle at 50% 0%, rgba(212, 175, 55, 0.15) 0%, transparent 60%)'
            }}
          />

          {/* Avatar Section - Centered */}
          <div className="relative z-10 flex flex-col items-center px-6">
            <AvatarUpload 
              user={user} 
              onUpdate={refreshUser}
              isSubscribed={user.subscription_active || false}
            />
            
            {/* User Info */}
            <motion.div 
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="text-center mt-5"
            >
              <h1 
                className="text-2xl font-black uppercase tracking-tight mb-1"
                style={applyAuraToPage && currentAuraColors ? {
                  textShadow: `0 0 30px ${currentAuraColors[0]}40`,
                } : {}}
              >
                {user.full_name}
              </h1>
              
              <div className="flex items-center justify-center gap-2 text-[#A1A1AA] text-sm">
                <Mail size={14} />
                <span className="truncate max-w-[200px]">{user.email}</span>
              </div>

              {user.auth_provider === 'google' && (
                <div className="mt-2 inline-flex items-center gap-1.5 text-xs text-[#71717A] bg-[#18181B] px-3 py-1.5 rounded-full">
                  <svg className="w-3 h-3" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  Google
                </div>
              )}

              {/* Subscription Badge */}
              {user.subscription_active && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.3 }}
                  className="mt-3 inline-flex items-center gap-1.5 bg-gradient-to-r from-[#D4AF37]/20 to-[#F59E0B]/20 border border-[#D4AF37]/40 px-3 py-1.5 rounded-full"
                >
                  <Crown size={14} className="text-[#D4AF37]" />
                  <span className="text-[#D4AF37] text-xs font-bold uppercase tracking-wider">FULL BRAVX</span>
                </motion.div>
              )}
            </motion.div>
          </div>
        </motion.div>

        {/* Bento Stats Grid - Mobile */}
        {stats && (
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="px-4 mb-4"
          >
            <div className="grid grid-cols-4 gap-2">
              {[
                { value: stats.tickets_count, label: 'Tickets', icon: Ticket, color: '#D4AF37' },
                { value: stats.events_count, label: 'Eventos', icon: Calendar, color: '#F59E0B' },
                { value: stats.purchases_count, label: 'Fotos', icon: Image, color: '#A855F7' },
                { value: stats.free_downloads_remaining, label: 'HD', icon: Download, color: '#22C55E' },
              ].map((stat, idx) => (
                <motion.div
                  key={stat.label}
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.4 + idx * 0.05 }}
                  className="relative overflow-hidden bg-[#121212] border border-[#27272A] p-3 text-center rounded-xl"
                  style={{
                    boxShadow: `inset 0 1px 0 0 rgba(255,255,255,0.05)`
                  }}
                >
                  {/* Icon glow */}
                  <div 
                    className="absolute -top-4 -right-4 w-12 h-12 rounded-full blur-xl opacity-20"
                    style={{ background: stat.color }}
                  />
                  <stat.icon size={16} className="mx-auto mb-1" style={{ color: stat.color }} />
                  <p className="text-xl font-black font-mono" style={{ color: stat.color === '#22C55E' ? stat.color : '#fff' }}>
                    {stat.value}
                  </p>
                  <p className="text-[9px] uppercase tracking-wider text-[#71717A]">{stat.label}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Sticky Glass Tabs Navigation - Mobile */}
        <div className="sticky top-0 z-30 bg-[#0A0A0A]/80 backdrop-blur-xl border-b border-[#27272A]/50">
          <div className="px-4 py-3">
            <div className="flex gap-1 bg-[#18181B] p-1 rounded-xl overflow-x-auto scrollbar-hide">
              {tabs.map((tab) => (
                <motion.button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`relative flex-shrink-0 flex flex-col items-center justify-center py-3 px-4 min-w-[64px] rounded-lg transition-all duration-300 ${
                    activeTab === tab.id
                      ? 'text-black'
                      : 'text-[#71717A]'
                  }`}
                  data-testid={`tab-mobile-${tab.id}`}
                >
                  {activeTab === tab.id && (
                    <motion.div
                      layoutId="activeTabMobile"
                      className="absolute inset-0 bg-gradient-to-r from-[#D4AF37] to-[#F59E0B] rounded-lg"
                      transition={{ type: "spring", stiffness: 500, damping: 35 }}
                    />
                  )}
                  <span className="relative z-10 flex flex-col items-center">
                    <tab.icon size={18} className={activeTab === tab.id ? 'text-black' : ''} />
                    <span className="text-[10px] font-bold uppercase tracking-wider mt-1">
                      {tab.id === 'subscription' ? 'BRAVX' : tab.label.split(' ').pop()}
                    </span>
                  </span>
                </motion.button>
              ))}
            </div>
          </div>
        </div>

        {/* Mobile Content Area */}
        <div className="px-4 py-4 pb-32">
          <AnimatePresence mode="wait">
            {/* Mobile Profile Tab */}
            {activeTab === 'profile' && (
              <motion.div
                key="profile-mobile"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.2 }}
                className="space-y-4"
              >
                {/* Edit Mode Header */}
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-bold uppercase tracking-tight">Mi Perfil</h2>
                  {!editMode ? (
                    <Button
                      onClick={startEdit}
                      size="sm"
                      className="bg-[#27272A] hover:bg-[#3F3F46] text-white rounded-lg"
                      data-testid="edit-profile-btn"
                    >
                      <Edit2 size={14} className="mr-1.5" />
                      Editar
                    </Button>
                  ) : (
                    <div className="flex gap-2">
                      <Button onClick={cancelEdit} size="sm" variant="ghost" className="text-[#71717A] rounded-lg">
                        <X size={16} />
                      </Button>
                      <Button
                        onClick={saveProfile}
                        disabled={saving}
                        size="sm"
                        className="bg-[#D4AF37] hover:bg-[#B8962E] text-black rounded-lg"
                        data-testid="save-profile-btn"
                      >
                        {saving ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
                      </Button>
                    </div>
                  )}
                </div>

                {/* Personal Info Card */}
                <div className="bg-[#121212] border border-[#27272A] rounded-2xl overflow-hidden">
                  <div className="p-4 border-b border-[#27272A] flex items-center gap-2">
                    <User size={16} className="text-[#D4AF37]" />
                    <span className="text-xs font-bold uppercase tracking-wider text-[#71717A]">Información Personal</span>
                  </div>
                  <div className="p-4 space-y-4">
                    {/* Name */}
                    <div>
                      <Label className="text-[10px] uppercase tracking-widest text-[#52525B] mb-1.5 block">Nombre</Label>
                      {editMode ? (
                        <Input
                          value={editData.full_name}
                          onChange={(e) => setEditData(prev => ({ ...prev, full_name: e.target.value }))}
                          className="bg-[#18181B] border-[#27272A] rounded-lg focus:border-[#D4AF37] focus:ring-[#D4AF37]/20"
                        />
                      ) : (
                        <p className="text-white font-medium">{user.full_name}</p>
                      )}
                    </div>
                    
                    {/* DNI */}
                    <div>
                      <Label className="text-[10px] uppercase tracking-widest text-[#52525B] mb-1.5 block">DNI</Label>
                      <p className="text-white font-mono text-lg">{user.dni || '-'}</p>
                    </div>

                    {/* Birth Date */}
                    <div>
                      <Label className="text-[10px] uppercase tracking-widest text-[#52525B] mb-1.5 block">Fecha de Nacimiento</Label>
                      <p className="text-white">
                        {user.birth_date ? format(new Date(user.birth_date + 'T00:00:00'), "d 'de' MMMM, yyyy", { locale: es }) : '-'}
                      </p>
                    </div>

                    {/* Phone */}
                    <div>
                      <Label className="text-[10px] uppercase tracking-widest text-[#52525B] mb-1.5 block">Teléfono</Label>
                      {editMode ? (
                        <Input
                          value={editData.phone}
                          onChange={(e) => setEditData(prev => ({ ...prev, phone: e.target.value }))}
                          placeholder="+54 11 1234-5678"
                          className="bg-[#18181B] border-[#27272A] rounded-lg focus:border-[#D4AF37] focus:ring-[#D4AF37]/20"
                        />
                      ) : (
                        <p className="text-white flex items-center gap-2">
                          {user.phone ? (
                            <>
                              <Phone size={14} className="text-[#52525B]" />
                              {user.phone}
                            </>
                          ) : '-'}
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Address Card */}
                <div className="bg-[#121212] border border-[#27272A] rounded-2xl overflow-hidden">
                  <div className="p-4 border-b border-[#27272A] flex items-center gap-2">
                    <MapPin size={16} className="text-[#F59E0B]" />
                    <span className="text-xs font-bold uppercase tracking-wider text-[#71717A]">Dirección</span>
                  </div>
                  <div className="p-4 space-y-4">
                    <div>
                      <Label className="text-[10px] uppercase tracking-widest text-[#52525B] mb-1.5 block">Dirección</Label>
                      {editMode ? (
                        <Input
                          value={editData.address}
                          onChange={(e) => setEditData(prev => ({ ...prev, address: e.target.value }))}
                          placeholder="Av. Corrientes 1234"
                          className="bg-[#18181B] border-[#27272A] rounded-lg focus:border-[#D4AF37] focus:ring-[#D4AF37]/20"
                        />
                      ) : (
                        <p className="text-white">{user.address || '-'}</p>
                      )}
                    </div>
                    <div>
                      <Label className="text-[10px] uppercase tracking-widest text-[#52525B] mb-1.5 block">Ciudad</Label>
                      {editMode ? (
                        <Input
                          value={editData.city}
                          onChange={(e) => setEditData(prev => ({ ...prev, city: e.target.value }))}
                          placeholder="Buenos Aires"
                          className="bg-[#18181B] border-[#27272A] rounded-lg focus:border-[#D4AF37] focus:ring-[#D4AF37]/20"
                        />
                      ) : (
                        <p className="text-white">{user.city || '-'}</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Linked Accounts Section - Only show if user has linked accounts */}
                {linkedAccounts.length > 0 && (
                  <div className="bg-gradient-to-br from-[#1a1a2e] to-[#16213e] border border-[#D4AF37]/30 rounded-2xl overflow-hidden">
                    <div className="p-4 border-b border-[#D4AF37]/20 flex items-center gap-2">
                      <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#D4AF37] to-[#B8962E] flex items-center justify-center">
                        <Shield size={16} className="text-black" />
                      </div>
                      <div>
                        <span className="text-xs font-bold uppercase tracking-wider text-[#D4AF37]">Tus Portales</span>
                        <p className="text-[10px] text-zinc-400">Acceso directo a tus cuentas</p>
                      </div>
                    </div>
                    <div className="p-4 space-y-2">
                      {linkedAccountsLoading ? (
                        <div className="flex items-center justify-center py-4">
                          <Loader2 size={20} className="animate-spin text-[#D4AF37]" />
                        </div>
                      ) : (
                        linkedAccounts.map((account) => (
                          <button
                            key={`${account.type}-${account.id}`}
                            onClick={() => handlePortalLogin(account)}
                            disabled={portalLoginLoading === account.type}
                            className="w-full flex items-center gap-3 p-3 bg-black/30 rounded-xl hover:bg-black/50 transition-all hover:scale-[1.02] border border-transparent hover:border-[#D4AF37]/30 disabled:opacity-50 disabled:cursor-wait"
                            data-testid={`linked-account-${account.type}`}
                          >
                            <div className="w-11 h-11 rounded-lg bg-[#27272A] flex items-center justify-center overflow-hidden border border-[#3F3F46]">
                              {account.logo ? (
                                <img src={account.logo} alt={account.name} className="w-full h-full object-cover" />
                              ) : (
                                <span className="text-lg font-black text-white/60">
                                  {account.name?.charAt(0)?.toUpperCase()}
                                </span>
                              )}
                            </div>
                            <div className="flex-1 min-w-0 text-left">
                              <div className="flex items-center gap-2">
                                <p className="font-bold text-sm text-white truncate">{account.name}</p>
                                {account.is_verified && (
                                  <CheckCircle size={14} className="text-[#D4AF37] flex-shrink-0" />
                                )}
                              </div>
                              <p className="text-[10px] text-[#D4AF37]/80 uppercase tracking-wider font-medium">
                                {account.label}
                              </p>
                            </div>
                            {portalLoginLoading === account.type ? (
                              <Loader2 size={18} className="text-[#D4AF37] animate-spin" />
                            ) : (
                              <ChevronRight size={18} className="text-[#D4AF37]" />
                            )}
                          </button>
                        ))
                      )}
                    </div>
                  </div>
                )}

                {/* Logout & Info */}
                {/* Quick Actions - DJ & Jobs Portal */}
                <div className="bg-[#121212] border border-[#27272A] rounded-2xl overflow-hidden">
                  <div className="p-4 border-b border-[#27272A] flex items-center gap-2">
                    <Sparkles size={16} className="text-[#D4AF37]" />
                    <span className="text-xs font-bold uppercase tracking-wider text-[#71717A]">
                      Accesos
                    </span>
                  </div>
                  <div className="p-4 space-y-3">
                    <Link 
                      to="/portal-dj"
                      className="flex items-center gap-3 p-3 bg-[#18181B] rounded-xl hover:bg-[#27272A] transition-colors"
                      data-testid="dj-portal-link"
                    >
                      <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-gradient-to-br from-[#D4AF37] to-[#B8962E]">
                        <Music size={20} className="text-black" />
                      </div>
                      <div className="flex-1">
                        <p className="font-bold text-sm">Portal DJ</p>
                        <p className="text-[10px] text-[#71717A]">Creá tu Press Kit profesional</p>
                      </div>
                      <ChevronRight size={16} className="text-[#52525B]" />
                    </Link>
                    
                    <Link 
                      to="/mi-cuenta/trabajo"
                      className="flex items-center gap-3 p-3 bg-[#18181B] rounded-xl hover:bg-[#27272A] transition-colors"
                      data-testid="jobs-portal-link"
                    >
                      <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-gradient-to-br from-purple-500 to-purple-700">
                        <Briefcase size={20} className="text-white" />
                      </div>
                      <div className="flex-1">
                        <p className="font-bold text-sm">Mi Perfil Laboral</p>
                        <p className="text-[10px] text-[#71717A]">Conectá con oportunidades</p>
                      </div>
                      <ChevronRight size={16} className="text-[#52525B]" />
                    </Link>
                  </div>
                </div>

                <div className="pt-4 space-y-3">
                  <Button
                    onClick={handleLogout}
                    variant="ghost"
                    className="w-full justify-center text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-xl py-6"
                    data-testid="logout-btn-mobile"
                  >
                    <LogOut size={18} className="mr-2" />
                    Cerrar Sesión
                  </Button>
                  
                  <p className="text-center text-[10px] uppercase tracking-widest text-[#3F3F46]">
                    <Clock size={10} className="inline mr-1" />
                    Miembro desde {user.created_at ? format(new Date(user.created_at), "MMM yyyy", { locale: es }) : ''}
                  </p>
                </div>
              </motion.div>
            )}

            {/* Mobile Subscription Tab */}
            {activeTab === 'bravx' && (
              <motion.div
                key="bravx-mobile"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.2 }}
              >
                <MyBravxSection />
              </motion.div>
            )}

            {activeTab === 'subscription' && (
              <motion.div
                key="subscription-mobile"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.2 }}
                className="space-y-4"
              >
                {/* Status Card */}
                <div className={`rounded-2xl overflow-hidden ${
                  user.subscription_active 
                    ? 'bg-gradient-to-br from-[#D4AF37]/10 via-[#121212] to-[#F59E0B]/10 border border-[#D4AF37]/30' 
                    : 'bg-[#121212] border border-[#27272A]'
                }`}>
                  <div className="p-5">
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold uppercase ${
                          user.subscription_active 
                            ? 'bg-[#D4AF37] text-black' 
                            : 'bg-[#27272A] text-[#71717A]'
                        }`}>
                          {user.subscription_active ? (
                            subscriptionData?.is_unlimited ? '∞ Vitalicia' :
                            subscriptionData?.is_in_trial ? '🎁 Prueba' : '✓ Activa'
                          ) : 'Sin Suscripción'}
                        </span>
                        <h3 className="text-xl font-black uppercase tracking-tight mt-2">FULL BRAVX</h3>
                      </div>
                      <div className="text-right">
                        {subscriptionData?.is_unlimited ? (
                          <motion.div
                            animate={{ scale: [1, 1.05, 1] }}
                            transition={{ duration: 2, repeat: Infinity }}
                          >
                            <InfinityIcon size={32} className="text-[#D4AF37] ml-auto" />
                          </motion.div>
                        ) : (
                          <>
                            <p className="text-2xl font-black">${subscriptionData?.price?.toLocaleString('es-AR') || '1.500'}</p>
                            <p className="text-[#71717A] text-xs">/mes</p>
                          </>
                        )}
                      </div>
                    </div>

                    {/* Trial Info */}
                    {subscriptionData?.is_in_trial && subscriptionData?.trial_ends_at && (
                      <div className="bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-xl p-3 mb-4">
                        <div className="flex items-center gap-2">
                          <Gift className="text-[#D4AF37] flex-shrink-0" size={18} />
                          <div className="text-sm">
                            <p className="text-white font-medium">Prueba activa</p>
                            <p className="text-[#A1A1AA] text-xs">
                              Termina el {format(new Date(subscriptionData.trial_ends_at), "d 'de' MMM", { locale: es })}
                            </p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Action Button */}
                    {user.subscription_active ? (
                      subscriptionData?.is_unlimited ? (
                        /* Unlimited Subscription - Premium Badge with Infinity Symbol */
                        <div className="flex flex-col items-center justify-center py-2">
                          <motion.div
                            className="relative flex items-center justify-center"
                            initial={{ scale: 0.8, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            transition={{ duration: 0.5 }}
                          >
                            {/* Animated Glow Ring */}
                            <motion.div
                              className="absolute inset-0 rounded-full"
                              style={{
                                background: 'radial-gradient(circle, rgba(212, 175, 55, 0.3) 0%, transparent 70%)',
                              }}
                              animate={{
                                scale: [1, 1.3, 1],
                                opacity: [0.5, 0.2, 0.5],
                              }}
                              transition={{
                                duration: 3,
                                repeat: Infinity,
                                ease: "easeInOut"
                              }}
                            />
                            {/* Infinity Icon */}
                            <motion.div
                              animate={{
                                rotate: [0, 5, -5, 0],
                              }}
                              transition={{
                                duration: 4,
                                repeat: Infinity,
                                ease: "easeInOut"
                              }}
                            >
                              <InfinityIcon size={40} className="text-[#D4AF37]" strokeWidth={2.5} />
                            </motion.div>
                          </motion.div>
                          <motion.p
                            className="text-[#D4AF37] text-xs font-bold uppercase tracking-widest mt-2"
                            animate={{ opacity: [0.7, 1, 0.7] }}
                            transition={{ duration: 2, repeat: Infinity }}
                          >
                            Suscripción Vitalicia
                          </motion.p>
                          <p className="text-[#52525B] text-[10px] mt-1">HD Ilimitadas</p>
                        </div>
                      ) : (
                        /* Regular Subscription - Show Cancel Button */
                        <div className="flex items-center justify-between">
                          <Button
                            variant="outline"
                            onClick={() => setShowCancelModal(true)}
                            className="border-red-500/30 text-red-400 hover:bg-red-500/10 rounded-lg"
                            size="sm"
                          >
                            Cancelar
                          </Button>
                          <span className="text-[#52525B] text-xs">
                            {subscriptionData?.free_hd_downloads_remaining || 0} HD restantes
                          </span>
                        </div>
                      )
                    ) : (
                      <div className="space-y-3">
                        {!subscriptionData?.has_used_free_trial && subscriptionData?.trial_days > 0 && (
                          <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-3 text-center">
                            <p className="text-green-400 font-bold text-sm">🎁 ¡{subscriptionData?.trial_days} días GRATIS!</p>
                          </div>
                        )}
                        <Button
                          onClick={handleSubscribe}
                          disabled={subscriptionLoading}
                          className="w-full bg-gradient-to-r from-[#D4AF37] to-[#F59E0B] text-black font-bold uppercase tracking-wider py-6 rounded-xl"
                        >
                          {subscriptionLoading ? (
                            <Loader2 className="animate-spin" size={18} />
                          ) : (
                            <>
                              <Crown className="mr-2" size={18} />
                              {subscriptionData?.has_used_free_trial 
                                ? 'Suscribirse' 
                                : 'Comenzar Gratis'}
                            </>
                          )}
                        </Button>
                      </div>
                    )}
                  </div>
                </div>

                {/* Benefits Grid */}
                <div className="bg-[#121212] border border-[#27272A] rounded-2xl overflow-hidden">
                  <div className="p-4 border-b border-[#27272A]">
                    <h4 className="text-sm font-bold uppercase tracking-tight flex items-center gap-2">
                      <Sparkles size={16} className="text-[#D4AF37]" />
                      Beneficios
                    </h4>
                  </div>
                  <div className="p-3 grid grid-cols-2 gap-2">
                    {[
                      { icon: Camera, title: '5 Fotos HD', desc: 'Gratis/mes' },
                      { icon: Sparkles, title: 'Auras', desc: 'Premium' },
                      { icon: Gift, title: 'Descuentos', desc: 'Exclusivos' },
                      { icon: Ticket, title: 'Sin Servicio', desc: 'En tandas' },
                      { icon: Clock, title: 'Acceso', desc: 'Prioritario' },
                      { icon: Crown, title: 'Partners', desc: 'Premium' },
                    ].map((benefit, idx) => (
                      <div 
                        key={idx}
                        className={`p-3 rounded-xl ${
                          user.subscription_active 
                            ? 'bg-[#1A1A1A] border border-[#D4AF37]/20' 
                            : 'bg-[#18181B] border border-[#27272A]'
                        }`}
                      >
                        <benefit.icon 
                          size={18} 
                          className={user.subscription_active ? 'text-[#D4AF37] mb-1.5' : 'text-[#52525B] mb-1.5'} 
                        />
                        <p className={`text-xs font-bold ${user.subscription_active ? 'text-white' : 'text-[#71717A]'}`}>
                          {benefit.title}
                        </p>
                        <p className="text-[10px] text-[#52525B]">{benefit.desc}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Cards Section */}
                <div className="bg-[#121212] border border-[#27272A] rounded-2xl overflow-hidden">
                  <div className="p-4 border-b border-[#27272A]">
                    <h4 className="text-sm font-bold uppercase tracking-tight flex items-center gap-2">
                      <CreditCard size={16} className="text-[#D4AF37]" />
                      Mis Tarjetas
                    </h4>
                  </div>
                  <div className="p-4">
                    <CardWallet />
                  </div>
                </div>
              </motion.div>
            )}

            {/* Mobile Benefits Tab */}
            {activeTab === 'benefits' && (
              <motion.div
                key="benefits-mobile"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.2 }}
                className="space-y-4"
              >
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-bold uppercase tracking-tight">Mis Beneficios</h2>
                  <Link 
                    to="/beneficios" 
                    className="text-[#D4AF37] text-xs font-bold uppercase flex items-center gap-1"
                  >
                    Ver todos <ChevronRight size={14} />
                  </Link>
                </div>

                {!user.subscription_active && (
                  <div className="bg-gradient-to-r from-[#D4AF37]/10 to-[#F59E0B]/10 border border-[#D4AF37]/30 p-4 rounded-2xl">
                    <div className="flex items-center gap-3">
                      <Crown size={24} className="text-[#D4AF37]" />
                      <div className="flex-1">
                        <p className="text-[#D4AF37] font-bold text-sm">FULL BRAVX</p>
                        <p className="text-[#A1A1AA] text-xs">Suscribite para acceder a beneficios exclusivos</p>
                      </div>
                      <ChevronRight size={18} className="text-[#D4AF37]" />
                    </div>
                  </div>
                )}

                {myBenefitsLoading ? (
                  <div className="space-y-3">
                    {[1, 2, 3].map((i) => (
                      <div key={i} className="bg-[#121212] border border-[#27272A] p-4 rounded-2xl animate-pulse">
                        <div className="h-5 bg-[#1A1A1A] w-1/2 mb-2 rounded" />
                        <div className="h-4 bg-[#1A1A1A] w-1/3 rounded" />
                      </div>
                    ))}
                  </div>
                ) : myBenefits.length === 0 ? (
                  <div className="bg-[#121212] border border-[#27272A] p-12 rounded-2xl text-center">
                    <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#1A1A1A] flex items-center justify-center">
                      <Gift size={28} className="text-[#3F3F46]" />
                    </div>
                    <h3 className="text-lg font-bold uppercase mb-2">Sin beneficios</h3>
                    <p className="text-[#71717A] text-sm mb-6">
                      {user.subscription_active 
                        ? 'Explorá los beneficios disponibles' 
                        : 'Suscribite a FULL BRAVX para acceder'}
                    </p>
                    <Link 
                      to="/beneficios" 
                      className="inline-flex items-center gap-2 bg-gradient-to-r from-[#D4AF37] to-[#F59E0B] text-black font-bold uppercase text-sm px-6 py-3 rounded-xl"
                    >
                      Ver Beneficios
                      <ChevronRight size={16} />
                    </Link>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {myBenefits.map((benefit, index) => (
                      <motion.div
                        key={benefit.id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.05 }}
                        className={`bg-[#121212] border rounded-2xl overflow-hidden ${
                          benefit.redeemed 
                            ? 'border-green-500/30' 
                            : 'border-[#27272A]'
                        }`}
                      >
                        {/* Header with status */}
                        <div 
                          className="h-1"
                          style={{
                            background: benefit.redeemed 
                              ? 'linear-gradient(90deg, #22C55E, #16A34A)'
                              : 'linear-gradient(90deg, #D4AF37, #F59E0B)'
                          }}
                        />
                        <div className="p-4">
                          <div className="flex items-start gap-3">
                            {/* Image or Icon */}
                            {benefit.benefit_image_url ? (
                              <img 
                                src={benefit.benefit_image_url} 
                                alt="" 
                                className="w-14 h-14 rounded-xl object-cover flex-shrink-0"
                              />
                            ) : (
                              <div className="w-14 h-14 rounded-xl bg-[#1A1A1A] flex items-center justify-center flex-shrink-0">
                                <Gift size={24} className="text-[#D4AF37]" />
                              </div>
                            )}
                            
                            <div className="flex-1 min-w-0">
                              <div className="flex items-start justify-between gap-2">
                                <div className="min-w-0">
                                  <h4 className="font-bold text-white truncate">{benefit.benefit_title}</h4>
                                  <p className="text-[#71717A] text-xs truncate">{benefit.partner_name}</p>
                                </div>
                                <span className={`px-2 py-1 text-[10px] font-bold uppercase rounded-full flex-shrink-0 ${
                                  benefit.redeemed
                                    ? 'bg-green-500/20 text-green-400 border border-green-500/30'
                                    : 'bg-[#D4AF37]/20 text-[#D4AF37] border border-[#D4AF37]/30'
                                }`}>
                                  {benefit.redeemed ? 'Canjeado' : 'Pendiente'}
                                </span>
                              </div>
                              
                              {/* Code */}
                              <div className="mt-2 flex items-center gap-2">
                                <span className="font-mono text-sm bg-[#1A1A1A] px-2 py-1 rounded text-[#D4AF37] font-bold">
                                  {benefit.code}
                                </span>
                                {benefit.redeemed && benefit.redeemed_at && (
                                  <span className="text-[10px] text-[#52525B]">
                                    {format(new Date(benefit.redeemed_at), "d MMM", { locale: es })}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                )}
              </motion.div>
            )}

            {/* Mobile Following Tab */}
            {activeTab === 'following' && (
              <motion.div
                key="following-mobile"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.2 }}
                className="space-y-4"
              >
                <h2 className="text-lg font-bold uppercase tracking-tight flex items-center gap-2">
                  <Heart size={18} className="text-[#D4AF37]" />
                  Productoras que sigo
                </h2>

                {followingLoading ? (
                  <div className="space-y-3">
                    {[1, 2, 3].map((i) => (
                      <div key={i} className="bg-[#121212] border border-[#27272A] p-4 rounded-2xl animate-pulse">
                        <div className="flex items-center gap-3">
                          <div className="w-14 h-14 bg-[#1A1A1A] rounded-xl" />
                          <div className="flex-1">
                            <div className="h-5 bg-[#1A1A1A] w-1/2 mb-2 rounded" />
                            <div className="h-4 bg-[#1A1A1A] w-1/3 rounded" />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : followingProducers.length === 0 ? (
                  <div className="bg-[#121212] border border-[#27272A] p-12 rounded-2xl text-center">
                    <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#1A1A1A] flex items-center justify-center">
                      <Heart size={28} className="text-[#3F3F46]" />
                    </div>
                    <h3 className="text-lg font-bold uppercase mb-2">Sin productoras</h3>
                    <p className="text-[#71717A] text-sm mb-6">Seguí productoras para enterarte de sus nuevos eventos</p>
                    <Link 
                      to="/eventos" 
                      className="inline-flex items-center gap-2 bg-gradient-to-r from-[#D4AF37] to-[#F59E0B] text-black font-bold uppercase text-sm px-6 py-3 rounded-xl"
                    >
                      Explorar Eventos
                      <ChevronRight size={16} />
                    </Link>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {followingProducers.map((producer, index) => (
                      <motion.div
                        key={producer.id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.05 }}
                        className="bg-[#121212] border border-[#27272A] rounded-2xl overflow-hidden"
                      >
                        <Link to={`/productora/${producer.slug}`} className="block p-4">
                          <div className="flex items-center gap-3">
                            {producer.branding?.logo_url ? (
                              <img 
                                src={producer.branding.logo_url} 
                                alt={producer.business_name} 
                                className="w-14 h-14 rounded-xl object-cover flex-shrink-0"
                              />
                            ) : (
                              <div className="w-14 h-14 rounded-xl bg-[#D4AF37]/10 border border-[#D4AF37]/30 flex items-center justify-center flex-shrink-0">
                                <span className="text-xl font-bold text-[#D4AF37]">
                                  {producer.business_name?.charAt(0)}
                                </span>
                              </div>
                            )}
                            
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <h4 className="font-bold text-white truncate">{producer.business_name}</h4>
                                {producer.is_verified && <ShieldCheck size={14} className="text-emerald-400 flex-shrink-0" />}
                              </div>
                              {(producer.city || producer.province) && (
                                <p className="text-[#71717A] text-xs truncate flex items-center gap-1">
                                  <MapPin size={10} />
                                  {[producer.city, producer.province].filter(Boolean).join(', ')}
                                </p>
                              )}
                              <div className="flex items-center gap-2 mt-1">
                                <span className="text-xs text-[#D4AF37]">
                                  {producer.followers_count || 0} seguidores
                                </span>
                              </div>
                            </div>
                            
                            <ChevronRight size={16} className="text-[#3F3F46]" />
                          </div>
                        </Link>
                        
                        <div className="px-4 pb-4">
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              handleUnfollow(producer.id);
                            }}
                            className="w-full py-2 px-4 bg-white/5 border border-white/10 rounded-lg text-sm text-white/60 hover:text-white hover:bg-white/10 transition-colors flex items-center justify-center gap-2"
                          >
                            <UserCheck size={14} />
                            Dejar de seguir
                          </button>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                )}
              </motion.div>
            )}

            {/* Mobile Tickets Tab */}
            {activeTab === 'tickets' && (
              <motion.div
                key="tickets-mobile"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.2 }}
                className="space-y-4"
              >
                <h2 className="text-lg font-bold uppercase tracking-tight">Mis Entradas</h2>

                {/* DNI Banner */}
                {user?.dni && tickets.length > 0 && (
                  <div className="bg-gradient-to-r from-green-500/10 to-emerald-500/10 border border-green-500/30 p-4 rounded-2xl">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-green-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                        <Shield size={20} className="text-green-500" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-green-400 font-bold text-xs uppercase tracking-wider">Tu DNI es tu entrada</p>
                        <p className="text-white text-lg font-mono font-bold truncate">{user.dni}</p>
                      </div>
                    </div>
                  </div>
                )}

                {ticketsLoading ? (
                  <div className="space-y-3">
                    {[1, 2, 3].map((i) => (
                      <div key={i} className="bg-[#121212] border border-[#27272A] p-4 rounded-2xl animate-pulse">
                        <div className="h-5 bg-[#1A1A1A] w-1/2 mb-2 rounded" />
                        <div className="h-4 bg-[#1A1A1A] w-1/3 rounded" />
                      </div>
                    ))}
                  </div>
                ) : tickets.length === 0 ? (
                  <div className="bg-[#121212] border border-[#27272A] p-12 rounded-2xl text-center">
                    <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#1A1A1A] flex items-center justify-center">
                      <Ticket size={28} className="text-[#3F3F46]" />
                    </div>
                    <h3 className="text-lg font-bold uppercase mb-2">Sin entradas</h3>
                    <p className="text-[#71717A] text-sm mb-6">¡Explora nuestros próximos eventos!</p>
                    <Link 
                      to="/eventos" 
                      className="inline-flex items-center gap-2 bg-gradient-to-r from-[#D4AF37] to-[#F59E0B] text-black font-bold uppercase text-sm px-6 py-3 rounded-xl"
                    >
                      Ver Eventos
                      <ChevronRight size={16} />
                    </Link>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {tickets.map((ticket, index) => (
                      <motion.div
                        key={ticket.id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.05 }}
                        onClick={() => setSelectedTicket(ticket)}
                        className="rounded-2xl overflow-hidden cursor-pointer active:scale-[0.98] transition-transform"
                      >
                        {/* Event image background */}
                        <div className="relative h-[130px]">
                          {ticket.event_image_url ? (
                            <img
                              src={ticket.event_image_url}
                              alt={ticket.event_title}
                              className="absolute inset-0 w-full h-full object-cover"
                            />
                          ) : (
                            <div className="absolute inset-0 bg-gradient-to-br from-zinc-800 to-zinc-900" />
                          )}
                          <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-black/10" />
                          
                          {/* Badges */}
                          <div className="absolute top-2 left-2 flex gap-1.5">
                            <span className={`px-2 py-0.5 text-[9px] font-bold uppercase rounded-full backdrop-blur-sm ${
                              isTicketValid(ticket)
                                ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                                : 'bg-zinc-500/20 text-zinc-400 border border-zinc-500/30'
                            }`}>
                              {isTicketValid(ticket) ? 'Valido' : 'Usado'}
                            </span>
                            {ticket.tier_name && (
                              <span className="px-2 py-0.5 text-[9px] font-bold uppercase rounded-full bg-[#D4AF37]/20 text-[#D4AF37] border border-[#D4AF37]/30 backdrop-blur-sm">
                                {ticket.tier_name}
                              </span>
                            )}
                          </div>

                          {/* Event info */}
                          <div className="absolute bottom-0 left-0 right-0 p-3">
                            <h4 className="font-bold text-white text-base truncate">{ticket.event_title}</h4>
                            <div className="flex items-center gap-2 text-zinc-300 text-[11px] mt-0.5">
                              {ticket.event_date && (
                                <span className="flex items-center gap-1">
                                  <Calendar size={10} className="text-[#D4AF37]" />
                                  {formatInTimeZone(new Date(ticket.event_date), 'America/Argentina/Buenos_Aires', "d MMM", { locale: es })}
                                </span>
                              )}
                              {(ticket.venue_name || ticket.event_location) && (
                                <span className="flex items-center gap-1 truncate">
                                  <MapPin size={10} className="text-[#D4AF37]" />
                                  {ticket.venue_name || ticket.event_location}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Bottom stub */}
                        <div className="bg-zinc-900 px-3 py-2 flex items-center justify-between border-t border-dashed border-zinc-700/40">
                          {(ticket.holder_dni || ticket.buyer_dni || user?.dni) && isTicketValid(ticket) ? (
                            <div className="flex items-center gap-1.5">
                              <Shield size={14} className="text-green-500" />
                              <span className="text-xs font-mono font-bold text-white">{ticket.holder_dni || ticket.buyer_dni || user?.dni}</span>
                            </div>
                          ) : (
                            <span className="font-mono text-[11px] text-zinc-500">{ticket.ticket_code}</span>
                          )}
                          <ChevronRight size={16} className="text-zinc-500" />
                        </div>
                      </motion.div>
                    ))}
                  </div>
                )}
              </motion.div>
            )}

            {/* Mobile Gallery Tab */}
            {activeTab === 'gallery' && (
              <motion.div
                key="gallery-mobile"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.2 }}
                className="space-y-4"
              >
                <h2 className="text-lg font-bold uppercase tracking-tight">Mis Fotos</h2>

                {galleryLoading ? (
                  <div className="grid grid-cols-3 gap-1">
                    {[1, 2, 3, 4, 5, 6].map((i) => (
                      <div key={i} className="aspect-square bg-[#121212] animate-pulse rounded-lg" />
                    ))}
                  </div>
                ) : galleryPurchases.length === 0 ? (
                  <div className="bg-[#121212] border border-[#27272A] p-12 rounded-2xl text-center">
                    <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#1A1A1A] flex items-center justify-center">
                      <Image size={28} className="text-[#3F3F46]" />
                    </div>
                    <h3 className="text-lg font-bold uppercase mb-2">Sin fotos</h3>
                    <p className="text-[#71717A] text-sm mb-6">Comprá fotos de tus eventos favoritos</p>
                    <Link 
                      to="/galeria" 
                      className="inline-flex items-center gap-2 bg-gradient-to-r from-[#D4AF37] to-[#F59E0B] text-black font-bold uppercase text-sm px-6 py-3 rounded-xl"
                    >
                      Ver Galería
                      <ChevronRight size={16} />
                    </Link>
                  </div>
                ) : (
                  <div className="grid grid-cols-3 gap-1 rounded-2xl overflow-hidden">
                    {galleryPurchases.map((purchase, index) => (
                      <motion.div
                        key={purchase.id}
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: index * 0.03 }}
                        className="relative aspect-square bg-[#121212] group cursor-pointer"
                        onClick={() => handleDownloadPurchase(purchase)}
                      >
                        <img
                          src={`${API_URL}/api/gallery/image/${purchase.file_id}?size=thumb`}
                          alt=""
                          className="w-full h-full object-cover"
                        />
                        {/* Overlay */}
                        <div className="absolute inset-0 bg-black/60 opacity-0 group-active:opacity-100 transition-opacity flex items-center justify-center">
                          {downloadingId === purchase.id ? (
                            <Loader2 size={24} className="animate-spin text-white" />
                          ) : (
                            <Download size={24} className="text-white" />
                          )}
                        </div>
                        {/* Status Badge */}
                        {purchase.status === 'completed' && (
                          <div className="absolute top-1 right-1 w-5 h-5 bg-green-500 rounded-full flex items-center justify-center">
                            <Check size={12} className="text-white" />
                          </div>
                        )}
                        {/* Type Badge */}
                        <div className={`absolute bottom-1 left-1 px-1.5 py-0.5 text-[8px] font-bold uppercase rounded ${
                          purchase.download_type === 'free' 
                            ? 'bg-green-500/80 text-white' 
                            : 'bg-[#D4AF37]/80 text-black'
                        }`}>
                          {purchase.download_type === 'free' ? 'HD' : 'PRO'}
                        </div>
                      </motion.div>
                    ))}
                  </div>
                )}

                {/* Download Info */}
                {galleryPurchases.length > 0 && (
                  <p className="text-center text-[10px] text-[#52525B] uppercase tracking-wider">
                    Toca una foto para descargar
                  </p>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* ========== DESKTOP LAYOUT - Original ========== */}
      <section className="hidden lg:block pt-32 pb-24 px-6 lg:px-12 relative z-10" data-testid="user-account-page">
        <div className="max-w-7xl mx-auto">
          {/* Bento Grid Layout */}
          <div className="grid grid-cols-12 gap-6">
            
            {/* Profile Card - Left Side */}
            <motion.div 
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
              className="col-span-4 xl:col-span-3"
            >
              <div 
                className="bg-[#121212] border p-8 sticky top-24 transition-all duration-500"
                style={applyAuraToPage && currentAuraColors ? {
                  borderColor: `${currentAuraColors[0]}40`,
                  boxShadow: `0 0 30px ${currentAuraColors[0]}10`,
                } : { borderColor: '#27272A' }}
              >
                {/* Avatar */}
                <div className="flex flex-col items-center text-center mb-8">
                  <AvatarUpload 
                    user={user} 
                    onUpdate={refreshUser}
                    isSubscribed={user.subscription_active || false}
                  />
                  
                  <motion.h1 
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.2 }}
                    className="text-2xl font-black uppercase tracking-tight mt-6 mb-1"
                    style={applyAuraToPage && currentAuraColors ? {
                      textShadow: `0 0 30px ${currentAuraColors[0]}30`,
                    } : {}}
                  >
                    {user.full_name}
                  </motion.h1>
                  
                  <motion.p 
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.3 }}
                    className="text-[#A1A1AA] text-sm flex items-center gap-1"
                  >
                    <Mail size={12} />
                    {user.email}
                  </motion.p>

                  {user.auth_provider === 'google' && (
                    <motion.div 
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: 0.4 }}
                      className="mt-3 flex items-center gap-1 text-xs text-[#52525B] bg-[#1A1A1A] px-3 py-1"
                    >
                      <svg className="w-3 h-3" viewBox="0 0 24 24">
                        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                        <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                      </svg>
                      Conectado con Google
                    </motion.div>
                  )}
                </div>

                {/* Quick Stats */}
                {stats && (
                  <div className="grid grid-cols-2 gap-3 mb-8">
                    <div className="bg-[#0A0A0A] p-4 text-center border border-[#1A1A1A]">
                      <p className="text-2xl font-black">{stats.tickets_count}</p>
                      <p className="text-[10px] uppercase tracking-widest text-[#52525B]">Tickets</p>
                    </div>
                    <div className="bg-[#0A0A0A] p-4 text-center border border-[#1A1A1A]">
                      <p className="text-2xl font-black">{stats.events_count}</p>
                      <p className="text-[10px] uppercase tracking-widest text-[#52525B]">Eventos</p>
                    </div>
                    <div className="bg-[#0A0A0A] p-4 text-center border border-[#1A1A1A]">
                      <p className="text-2xl font-black">{stats.purchases_count}</p>
                      <p className="text-[10px] uppercase tracking-widest text-[#52525B]">Fotos</p>
                    </div>
                    <div className="bg-[#0A0A0A] p-4 text-center border border-[#1A1A1A]">
                      <p className="text-2xl font-black text-green-500">{stats.free_downloads_remaining}</p>
                      <p className="text-[10px] uppercase tracking-widest text-[#52525B]">HD Gratis</p>
                    </div>
                  </div>
                )}

                {/* Navigation Tabs - vertical on desktop */}
                <nav className="space-y-2">
                  {tabs.map((tab) => (
                    <motion.button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      whileHover={{ x: 4 }}
                      whileTap={{ scale: 0.98 }}
                      className={`w-full flex items-center justify-between p-4 text-left transition-all duration-200 ${
                        activeTab === tab.id
                          ? 'bg-white text-black'
                          : 'bg-transparent text-[#A1A1AA] hover:text-white hover:bg-[#1A1A1A]'
                      }`}
                      data-testid={`tab-${tab.id}`}
                    >
                      <span className="flex items-center gap-3">
                        <tab.icon size={18} />
                        <span className="text-sm font-medium uppercase tracking-widest">{tab.label}</span>
                      </span>
                      <ChevronRight size={16} className={activeTab === tab.id ? 'opacity-100' : 'opacity-0'} />
                    </motion.button>
                  ))}
                </nav>

                {/* Logout Button */}
                <Button
                  onClick={handleLogout}
                  variant="ghost"
                  className="w-full mt-6 text-[#52525B] hover:text-red-500 hover:bg-red-500/10 justify-start"
                  data-testid="logout-btn"
                >
                  <LogOut size={18} className="mr-3" />
                  Cerrar Sesión
                </Button>

                {/* Member since */}
                <p className="text-[#3F3F46] text-[10px] uppercase tracking-widest mt-6 text-center">
                  <Clock size={10} className="inline mr-1" />
                  Miembro desde {user.created_at ? format(new Date(user.created_at), "MMM yyyy", { locale: es }) : ''}
                </p>
              </div>
            </motion.div>

            {/* Content Area - Right Side */}
            <motion.div 
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="col-span-8 xl:col-span-9"
            >

              <AnimatePresence mode="wait">
                {/* Profile Tab */}
                {activeTab === 'profile' && (
                  <motion.div
                    key="profile"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.3 }}
                    data-testid="profile-section"
                  >
                    <div className="flex items-center justify-between mb-8">
                      <div>
                        <h2 className="text-3xl font-black uppercase tracking-tight">Mi Perfil</h2>
                        <p className="text-[#52525B] text-sm">Gestiona tu información personal</p>
                      </div>
                      {!editMode ? (
                        <Button
                          onClick={startEdit}
                          className="btn-secondary"
                          data-testid="edit-profile-btn"
                        >
                          <Edit2 size={16} className="mr-2" />
                          Editar
                        </Button>
                      ) : (
                        <div className="flex gap-2">
                          <Button onClick={cancelEdit} variant="ghost" className="text-[#A1A1AA]">
                            <X size={18} />
                          </Button>
                          <Button
                            onClick={saveProfile}
                            disabled={saving}
                            className="btn-primary"
                            data-testid="save-profile-btn"
                          >
                            {saving ? <Loader2 size={16} className="animate-spin mr-2" /> : <Check size={16} className="mr-2" />}
                            Guardar
                          </Button>
                        </div>
                      )}
                    </div>

                    {/* Profile Info Cards */}
                    <div className="grid gap-6">
                      {/* Personal Info */}
                      <div className="bg-[#121212] border border-[#27272A] p-8">
                        <h3 className="text-sm font-bold uppercase tracking-widest text-[#52525B] mb-6 flex items-center gap-2">
                          <User size={14} />
                          Información Personal
                        </h3>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                          <div>
                            <Label className="text-xs uppercase tracking-widest text-[#52525B] mb-3 block">
                              Nombre Completo
                            </Label>
                            {editMode ? (
                              <Input
                                value={editData.full_name}
                                onChange={(e) => setEditData(prev => ({ ...prev, full_name: e.target.value }))}
                                className="input-bravo"
                              />
                            ) : (
                              <p className="text-white text-lg">{user.full_name}</p>
                            )}
                          </div>
                          
                          <div>
                            <Label className="text-xs uppercase tracking-widest text-[#52525B] mb-3 block">
                              Email
                            </Label>
                            <p className="text-white text-lg">{user.email}</p>
                            <span className="inline-flex items-center gap-1 text-xs text-green-500 mt-1">
                              <Shield size={10} /> Verificado
                            </span>
                          </div>
                          
                          <div>
                            <Label className="text-xs uppercase tracking-widest text-[#52525B] mb-3 block">
                              DNI
                            </Label>
                            <p className="text-white text-lg font-mono">{user.dni || '-'}</p>
                          </div>
                          
                          <div>
                            <Label className="text-xs uppercase tracking-widest text-[#52525B] mb-3 block">
                              Fecha de Nacimiento
                            </Label>
                            <p className="text-white text-lg">
                              {user.birth_date ? format(new Date(user.birth_date + 'T00:00:00'), "d 'de' MMMM, yyyy", { locale: es }) : '-'}
                            </p>
                          </div>
                          
                          <div>
                            <Label className="text-xs uppercase tracking-widest text-[#52525B] mb-3 block">
                              Teléfono
                            </Label>
                            {editMode ? (
                              <Input
                                value={editData.phone}
                                onChange={(e) => setEditData(prev => ({ ...prev, phone: e.target.value }))}
                                placeholder="+54 11 1234-5678"
                                className="input-bravo"
                              />
                            ) : (
                              <p className="text-white text-lg flex items-center gap-2">
                                {user.phone ? (
                                  <>
                                    <Phone size={14} className="text-[#52525B]" />
                                    {user.phone}
                                  </>
                                ) : '-'}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Address Info */}
                      <div className="bg-[#121212] border border-[#27272A] p-8">
                        <h3 className="text-sm font-bold uppercase tracking-widest text-[#52525B] mb-6 flex items-center gap-2">
                          <MapPin size={14} />
                          Dirección (opcional)
                        </h3>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                          <div>
                            <Label className="text-xs uppercase tracking-widest text-[#52525B] mb-3 block">
                              Dirección
                            </Label>
                            {editMode ? (
                              <Input
                                value={editData.address}
                                onChange={(e) => setEditData(prev => ({ ...prev, address: e.target.value }))}
                                placeholder="Av. Corrientes 1234"
                                className="input-bravo"
                              />
                            ) : (
                              <p className="text-white text-lg">{user.address || '-'}</p>
                            )}
                          </div>
                          
                          <div>
                            <Label className="text-xs uppercase tracking-widest text-[#52525B] mb-3 block">
                              Ciudad
                            </Label>
                            {editMode ? (
                              <Input
                                value={editData.city}
                                onChange={(e) => setEditData(prev => ({ ...prev, city: e.target.value }))}
                                placeholder="Buenos Aires"
                                className="input-bravo"
                              />
                            ) : (
                              <p className="text-white text-lg">{user.city || '-'}</p>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Linked Accounts Section - Only show if user has linked accounts (Desktop) */}
                      {linkedAccounts.length > 0 && (
                        <div className="bg-gradient-to-br from-[#1a1a2e] to-[#16213e] border border-[#D4AF37]/30 p-8 rounded-xl">
                          <h3 className="text-sm font-bold uppercase tracking-widest text-[#D4AF37] mb-6 flex items-center gap-3">
                            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#D4AF37] to-[#B8962E] flex items-center justify-center">
                              <Shield size={18} className="text-black" />
                            </div>
                            <div>
                              <span>Tus Portales Vinculados</span>
                              <p className="text-xs text-zinc-400 font-normal normal-case mt-0.5">Acceso directo con tu cuenta</p>
                            </div>
                          </h3>
                          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
                            {linkedAccountsLoading ? (
                              <div className="col-span-full flex items-center justify-center py-8">
                                <Loader2 size={24} className="animate-spin text-[#D4AF37]" />
                              </div>
                            ) : (
                              linkedAccounts.map((account) => (
                                <button
                                  key={`${account.type}-${account.id}-desktop`}
                                  onClick={() => handlePortalLogin(account)}
                                  disabled={portalLoginLoading === account.type}
                                  className="flex items-center gap-4 p-5 bg-black/30 rounded-xl hover:bg-black/50 transition-all hover:scale-[1.02] border border-transparent hover:border-[#D4AF37]/40 group disabled:opacity-50 disabled:cursor-wait text-left"
                                  data-testid={`linked-account-${account.type}-desktop`}
                                >
                                  <div className="w-14 h-14 rounded-xl bg-[#27272A] flex items-center justify-center overflow-hidden border-2 border-[#3F3F46] group-hover:border-[#D4AF37]/50 transition-colors">
                                    {account.logo ? (
                                      <img src={account.logo} alt={account.name} className="w-full h-full object-cover" />
                                    ) : (
                                      <span className="text-2xl font-black text-white/60">
                                        {account.name?.charAt(0)?.toUpperCase()}
                                      </span>
                                    )}
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2">
                                      <p className="font-bold text-lg text-white truncate">{account.name}</p>
                                      {account.is_verified && (
                                        <CheckCircle size={16} className="text-[#D4AF37] flex-shrink-0" />
                                      )}
                                    </div>
                                    <p className="text-xs text-[#D4AF37]/80 uppercase tracking-wider font-medium mt-1">
                                      {account.label}
                                    </p>
                                  </div>
                                  {portalLoginLoading === account.type ? (
                                    <Loader2 size={22} className="text-[#D4AF37] animate-spin" />
                                  ) : (
                                    <ChevronRight size={22} className="text-[#D4AF37] group-hover:translate-x-1 transition-transform" />
                                  )}
                                </button>
                              ))
                            )}
                          </div>
                        </div>
                      )}

                      {/* Quick Access - DJ, Jobs & Press Portal (Desktop) */}
                      <div className="bg-[#121212] border border-[#27272A] p-8">
                        <h3 className="text-sm font-bold uppercase tracking-widest text-[#52525B] mb-6 flex items-center gap-2">
                          <Sparkles size={14} className="text-[#D4AF37]" />
                          Accesos
                        </h3>
                        <div className="grid grid-cols-3 gap-4">
                          <Link 
                            to="/portal-dj"
                            className="flex items-center gap-4 p-5 bg-[#18181B] rounded-xl hover:bg-[#27272A] transition-colors group"
                            data-testid="dj-portal-link-desktop"
                          >
                            <div className="w-14 h-14 rounded-xl flex items-center justify-center group-hover:scale-105 transition-transform bg-gradient-to-br from-[#D4AF37] to-[#B8962E]">
                              <Music size={28} className="text-black" />
                            </div>
                            <div className="flex-1">
                              <p className="font-bold text-lg">Portal DJ</p>
                              <p className="text-sm text-[#71717A]">Creá tu Press Kit profesional</p>
                            </div>
                            <ChevronRight size={20} className="text-[#52525B] group-hover:text-white transition-colors" />
                          </Link>
                          
                          <Link 
                            to="/mi-cuenta/trabajo"
                            className="flex items-center gap-4 p-5 bg-[#18181B] rounded-xl hover:bg-[#27272A] transition-colors group"
                            data-testid="jobs-portal-link-desktop"
                          >
                            <div className="w-14 h-14 rounded-xl flex items-center justify-center group-hover:scale-105 transition-transform bg-gradient-to-br from-purple-500 to-purple-700">
                              <Briefcase size={28} className="text-white" />
                            </div>
                            <div className="flex-1">
                              <p className="font-bold text-lg">Mi Perfil Laboral</p>
                              <p className="text-sm text-[#71717A]">Conectá con oportunidades</p>
                            </div>
                            <ChevronRight size={20} className="text-[#52525B] group-hover:text-white transition-colors" />
                          </Link>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                )}

                {/* BRAVX Tab — Desktop */}
                {activeTab === 'bravx' && (
                  <motion.div
                    key="bravx-desktop"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.3 }}
                    data-testid="bravx-section-desktop"
                  >
                    <div className="mb-8">
                      <h2 className="text-2xl font-black uppercase tracking-tight mb-2 flex items-center gap-2">
                        <Sparkles className="text-[#D4AF37]" /> BRAVX Points
                      </h2>
                      <p className="text-sm text-white/50">Sumá puntos cuando otros compran usando tu @usuario.</p>
                    </div>
                    <MyBravxSection />
                  </motion.div>
                )}

                {/* Subscription Tab - FULL BRAVX */}
                {activeTab === 'subscription' && (
                  <motion.div
                    key="subscription"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.3 }}
                    data-testid="subscription-section"
                  >
                    <div className="mb-8">
                      <h2 className="text-2xl font-black uppercase tracking-tight mb-2 flex items-center gap-2">
                        <Crown className="text-[#D4AF37]" />
                        FULL BRAVX
                      </h2>
                      <p className="text-[#A1A1AA]">Gestioná tu suscripción premium</p>
                    </div>

                    {/* Current Status Card */}
                    <div className={`p-6 mb-6 border ${
                      user.subscription_active 
                        ? 'bg-gradient-to-br from-[#D4AF37]/10 to-[#D4AF37]/5 border-[#D4AF37]/30' 
                        : 'bg-[#121212] border-[#27272A]'
                    }`}>
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <div className="flex items-center gap-2 mb-2">
                            {user.subscription_active ? (
                              <span className="px-3 py-1 bg-[#D4AF37] text-black text-xs font-bold uppercase">
                                {subscriptionData?.is_unlimited ? '∞ Vitalicia' :
                                 subscriptionData?.is_in_trial ? '🎁 Período de Prueba' : '✓ Activa'}
                              </span>
                            ) : (
                              <span className="px-3 py-1 bg-[#27272A] text-[#A1A1AA] text-xs font-bold uppercase">
                                Sin Suscripción
                              </span>
                            )}
                          </div>
                          <h3 className="text-xl font-bold text-white">
                            {subscriptionData?.subscription_name || 'FULL BRAVX'}
                          </h3>
                        </div>
                        <div className="text-right">
                          {subscriptionData?.is_unlimited ? (
                            <motion.div
                              animate={{ scale: [1, 1.05, 1] }}
                              transition={{ duration: 2, repeat: Infinity }}
                            >
                              <InfinityIcon size={40} className="text-[#D4AF37] ml-auto" />
                            </motion.div>
                          ) : (
                            <>
                              <p className="text-3xl font-black text-white">
                                ${subscriptionData?.price?.toLocaleString('es-AR') || '1.500'}
                              </p>
                              <p className="text-[#A1A1AA] text-sm">/mes</p>
                            </>
                          )}
                        </div>
                      </div>
                      
                      {subscriptionData?.is_in_trial && subscriptionData?.trial_ends_at && (
                        <div className="bg-[#D4AF37]/20 border border-[#D4AF37]/30 p-4 mb-4">
                          <div className="flex items-center gap-2">
                            <Gift className="text-[#D4AF37]" size={20} />
                            <div>
                              <p className="text-white font-bold">Período de prueba activo</p>
                              <p className="text-[#A1A1AA] text-sm">
                                Tu prueba gratis termina el {format(new Date(subscriptionData.trial_ends_at), "d 'de' MMMM", { locale: es })}. 
                                Después se cobrará ${subscriptionData?.price?.toLocaleString('es-AR')}/mes.
                              </p>
                            </div>
                          </div>
                        </div>
                      )}

                      {user.subscription_active ? (
                        subscriptionData?.is_unlimited ? (
                          /* Unlimited Subscription - Premium Badge with Infinity Symbol */
                          <div className="flex items-center gap-6">
                            <motion.div
                              className="relative flex items-center justify-center"
                              initial={{ scale: 0.8, opacity: 0 }}
                              animate={{ scale: 1, opacity: 1 }}
                              transition={{ duration: 0.5 }}
                            >
                              {/* Animated Glow Ring */}
                              <motion.div
                                className="absolute -inset-4 rounded-full"
                                style={{
                                  background: 'radial-gradient(circle, rgba(212, 175, 55, 0.4) 0%, transparent 70%)',
                                }}
                                animate={{
                                  scale: [1, 1.4, 1],
                                  opacity: [0.6, 0.2, 0.6],
                                }}
                                transition={{
                                  duration: 3,
                                  repeat: Infinity,
                                  ease: "easeInOut"
                                }}
                              />
                              {/* Infinity Icon */}
                              <motion.div
                                animate={{
                                  rotate: [0, 5, -5, 0],
                                }}
                                transition={{
                                  duration: 4,
                                  repeat: Infinity,
                                  ease: "easeInOut"
                                }}
                              >
                                <InfinityIcon size={48} className="text-[#D4AF37]" strokeWidth={2.5} />
                              </motion.div>
                            </motion.div>
                            <div>
                              <motion.p
                                className="text-[#D4AF37] text-lg font-black uppercase tracking-widest"
                                animate={{ opacity: [0.7, 1, 0.7] }}
                                transition={{ duration: 2, repeat: Infinity }}
                              >
                                Suscripción Vitalicia
                              </motion.p>
                              <p className="text-[#52525B] text-sm">Acceso ilimitado a todos los beneficios • HD sin límites</p>
                            </div>
                          </div>
                        ) : subscriptionData?.status === 'cancelled' ? (
                          /* Cancelled but still active - Show Reactivate */
                          <div className="space-y-3">
                            <div className="bg-yellow-500/10 border border-yellow-500/30 p-3 rounded-lg">
                              <p className="text-yellow-400 font-semibold">Suscripción cancelada</p>
                              <p className="text-yellow-400/70 text-sm">
                                Seguís teniendo acceso hasta el {subscriptionData?.cancellation_effective_at ? 
                                  new Date(subscriptionData.cancellation_effective_at).toLocaleDateString('es-AR') : 
                                  'fin del período'}
                                {subscriptionData?.days_until_expiry > 0 && ` (${subscriptionData.days_until_expiry} días)`}
                              </p>
                            </div>
                            <Button
                              onClick={handleReactivateSubscription}
                              className="w-full bg-[#D4AF37] text-black hover:bg-[#B8962E] font-bold uppercase tracking-wider"
                              data-testid="reactivate-subscription-btn"
                            >
                              <Crown className="mr-2" size={18} />
                              Reactivar Suscripción
                            </Button>
                            <p className="text-[#52525B] text-xs text-center">
                              Recuperá tu suscripción sin perder ningún beneficio
                            </p>
                          </div>
                        ) : (
                          /* Regular Subscription - Show Cancel Button */
                          <div className="space-y-4">
                            {/* Next billing date info */}
                            <div className="bg-zinc-800/50 border border-zinc-700 rounded-lg p-4">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                  <Calendar className="text-[#D4AF37]" size={20} />
                                  <div>
                                    <p className="text-white font-medium">Próxima renovación</p>
                                    <p className="text-[#A1A1AA] text-sm">
                                      {subscriptionData?.next_billing_date 
                                        ? new Date(subscriptionData.next_billing_date).toLocaleDateString('es-AR', { day: 'numeric', month: 'long', year: 'numeric' })
                                        : user.subscription_expires 
                                          ? new Date(user.subscription_expires).toLocaleDateString('es-AR', { day: 'numeric', month: 'long', year: 'numeric' })
                                          : 'Próximamente'
                                      }
                                    </p>
                                  </div>
                                </div>
                                <div className="text-right">
                                  <p className="text-[#D4AF37] font-bold">${subscriptionData?.price?.toLocaleString('es-AR') || '1.500'}</p>
                                  <p className="text-[#52525B] text-xs">Renovación automática</p>
                                </div>
                              </div>
                            </div>
                            
                            <div className="flex flex-col sm:flex-row gap-3 items-center justify-between">
                              <p className="text-[#52525B] text-xs">
                                {subscriptionData?.free_hd_downloads_remaining || 0} descargas HD restantes este mes
                              </p>
                              <Button
                                variant="outline"
                                onClick={() => setShowCancelModal(true)}
                                className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                                data-testid="cancel-subscription-btn"
                              >
                                Cancelar Suscripción
                              </Button>
                            </div>
                          </div>
                        )
                      ) : (
                        <div>
                          {!subscriptionData?.has_used_free_trial && subscriptionData?.trial_days > 0 && (
                            <div className="bg-green-500/10 border border-green-500/30 p-3 mb-4 text-center">
                              <p className="text-green-400 font-bold">🎁 ¡{subscriptionData?.trial_days} días GRATIS!</p>
                              <p className="text-green-400/70 text-sm">Prueba todos los beneficios sin costo</p>
                            </div>
                          )}
                          <Button
                            onClick={handleSubscribe}
                            disabled={subscriptionLoading}
                            className="w-full bg-[#D4AF37] text-black hover:bg-[#B8962E] font-bold uppercase tracking-wider py-6"
                            data-testid="subscribe-btn"
                          >
                            {subscriptionLoading ? (
                              <Loader2 className="animate-spin mr-2" size={20} />
                            ) : (
                              <Crown className="mr-2" size={20} />
                            )}
                            {subscriptionData?.has_used_free_trial || !subscriptionData?.trial_days
                              ? `Suscribirse por $${subscriptionData?.price?.toLocaleString('es-AR')}/mes`
                              : `Comenzar ${subscriptionData?.trial_days} Días Gratis`
                            }
                          </Button>
                          <p className="text-[#52525B] text-xs text-center mt-2">
                            {subscriptionData?.has_used_free_trial || !subscriptionData?.trial_days
                              ? 'Se cobra mensualmente. Cancelá cuando quieras.'
                              : `Se activa inmediatamente. Se cobra $${subscriptionData?.price?.toLocaleString('es-AR')}/mes después de ${subscriptionData?.trial_days} días.`
                            }
                          </p>
                        </div>
                      )}
                    </div>

                    {/* Benefits Grid */}
                    <div className="mb-6">
                      <h4 className="text-lg font-bold uppercase tracking-tight mb-4 flex items-center gap-2">
                        <Sparkles className="text-[#D4AF37]" size={18} />
                        Beneficios Incluidos
                      </h4>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {[
                          { icon: Camera, title: '5 Fotos HD Gratis/Mes', desc: 'Descargá fotos en alta calidad' },
                          { icon: Sparkles, title: 'Auras Premium', desc: 'Personaliza tu perfil con estilos exclusivos' },
                          { icon: Gift, title: 'Descuentos Exclusivos', desc: 'Accedé a códigos de descuento especiales' },
                          { icon: Ticket, title: 'Ahorro en Tandas', desc: 'Sin cargo de servicio en tandas seleccionadas' },
                          { icon: Clock, title: 'Acceso Prioritario', desc: 'Comprá entradas antes que nadie' },
                          { icon: Crown, title: 'Partners Premium', desc: 'Beneficios en locales asociados' },
                        ].map((benefit, idx) => (
                          <div 
                            key={idx}
                            className={`p-4 border ${
                              user.subscription_active 
                                ? 'bg-[#1A1A1A] border-[#D4AF37]/20' 
                                : 'bg-[#121212] border-[#27272A]'
                            }`}
                          >
                            <div className="flex items-start gap-3">
                              <div className={`p-2 ${user.subscription_active ? 'bg-[#D4AF37]/20' : 'bg-[#27272A]'}`}>
                                <benefit.icon size={18} className={user.subscription_active ? 'text-[#D4AF37]' : 'text-[#52525B]'} />
                              </div>
                              <div>
                                <p className={`font-bold ${user.subscription_active ? 'text-white' : 'text-[#A1A1AA]'}`}>
                                  {benefit.title}
                                </p>
                                <p className="text-[#52525B] text-sm">{benefit.desc}</p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* FAQ */}
                    {/* Card Wallet Section */}
                    <div className="bg-[#121212] border border-[#27272A] p-6 mb-6">
                      <h4 className="font-bold mb-4 flex items-center gap-2">
                        <CreditCard size={20} className="text-[#D4AF37]" />
                        Mis Tarjetas
                      </h4>
                      <p className="text-[#A1A1AA] text-sm mb-4">
                        Guardá tus tarjetas para pagar más rápido
                      </p>
                      <CardWallet />
                    </div>

                    <div className="bg-[#121212] border border-[#27272A] p-6">
                      <h4 className="font-bold mb-4">Preguntas Frecuentes</h4>
                      <div className="space-y-4 text-sm">
                        <div>
                          <p className="text-white font-medium">¿Cuándo se cobra?</p>
                          <p className="text-[#A1A1AA]">
                            {subscriptionData?.has_used_free_trial || !subscriptionData?.trial_days
                              ? 'Se cobra inmediatamente y luego cada mes en la misma fecha.'
                              : `Tenés ${subscriptionData?.trial_days} días gratis. El primer cobro es después del período de prueba.`
                            }
                          </p>
                        </div>
                        <div>
                          <p className="text-white font-medium">¿Puedo cancelar cuando quiera?</p>
                          <p className="text-[#A1A1AA]">Sí, podés cancelar en cualquier momento desde esta página. No hay permanencia mínima.</p>
                        </div>
                        <div>
                          <p className="text-white font-medium">¿Qué pasa si cancelo durante el período de prueba?</p>
                          <p className="text-[#A1A1AA]">No se te cobra nada. Podés disfrutar los beneficios hasta que termine el período.</p>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                )}

                {/* My Benefits Tab - Desktop */}
                {activeTab === 'benefits' && (
                  <motion.div
                    key="benefits"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.3 }}
                    data-testid="benefits-section"
                  >
                    <div className="mb-8 flex items-start justify-between">
                      <div>
                        <h2 className="text-3xl font-black uppercase tracking-tight flex items-center gap-2">
                          <Gift className="text-[#D4AF37]" />
                          Mis Beneficios
                        </h2>
                        <p className="text-[#52525B] text-sm">Los beneficios que canjeaste o reclamaste</p>
                      </div>
                      <Link 
                        to="/beneficios" 
                        className="inline-flex items-center gap-2 text-[#D4AF37] text-sm font-bold uppercase hover:underline"
                      >
                        Ver todos los beneficios <ChevronRight size={16} />
                      </Link>
                    </div>

                    {!user.subscription_active && (
                      <div className="bg-gradient-to-r from-[#D4AF37]/10 to-[#F59E0B]/10 border border-[#D4AF37]/30 p-6 mb-6">
                        <div className="flex items-center gap-4">
                          <div className="p-3 bg-[#D4AF37]/20 rounded-full">
                            <Crown size={28} className="text-[#D4AF37]" />
                          </div>
                          <div className="flex-1">
                            <h3 className="text-[#D4AF37] font-bold text-lg">FULL BRAVX</h3>
                            <p className="text-[#A1A1AA] text-sm">Suscribite para acceder a beneficios exclusivos de nuestros partners</p>
                          </div>
                          <Button
                            onClick={() => setActiveTab('subscription')}
                            className="bg-gradient-to-r from-[#D4AF37] to-[#F59E0B] text-black font-bold"
                          >
                            Suscribirse
                          </Button>
                        </div>
                      </div>
                    )}

                    {myBenefitsLoading ? (
                      <div className="grid grid-cols-2 gap-4">
                        {[1, 2, 3, 4].map((i) => (
                          <div key={i} className="bg-[#121212] border border-[#27272A] p-6 animate-pulse">
                            <div className="flex gap-4">
                              <div className="w-16 h-16 bg-[#1A1A1A] rounded-lg" />
                              <div className="flex-1">
                                <div className="h-5 bg-[#1A1A1A] w-2/3 mb-2" />
                                <div className="h-4 bg-[#1A1A1A] w-1/3" />
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : myBenefits.length === 0 ? (
                      <div className="bg-[#121212] border border-[#27272A] p-16 text-center">
                        <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-[#1A1A1A] flex items-center justify-center">
                          <Gift size={36} className="text-[#3F3F46]" />
                        </div>
                        <h3 className="text-xl font-bold uppercase mb-2">Sin beneficios canjeados</h3>
                        <p className="text-[#71717A] text-sm mb-6 max-w-md mx-auto">
                          {user.subscription_active 
                            ? 'Todavía no reclamaste ningún beneficio. Explorá los beneficios disponibles y empezá a disfrutar.' 
                            : 'Suscribite a FULL BRAVX para acceder a beneficios exclusivos de nuestros partners.'}
                        </p>
                        <Link 
                          to="/beneficios" 
                          className="inline-flex items-center gap-2 bg-gradient-to-r from-[#D4AF37] to-[#F59E0B] text-black font-bold uppercase text-sm px-8 py-4"
                        >
                          Explorar Beneficios
                          <ChevronRight size={16} />
                        </Link>
                      </div>
                    ) : (
                      <>
                        {/* Stats */}
                        <div className="grid grid-cols-3 gap-4 mb-6">
                          <div className="bg-[#121212] border border-[#27272A] p-4 text-center">
                            <p className="text-3xl font-black text-[#D4AF37]">{myBenefits.length}</p>
                            <p className="text-[10px] uppercase tracking-widest text-[#52525B]">Total</p>
                          </div>
                          <div className="bg-[#121212] border border-[#27272A] p-4 text-center">
                            <p className="text-3xl font-black text-green-400">{myBenefits.filter(b => b.redeemed).length}</p>
                            <p className="text-[10px] uppercase tracking-widest text-[#52525B]">Canjeados</p>
                          </div>
                          <div className="bg-[#121212] border border-[#27272A] p-4 text-center">
                            <p className="text-3xl font-black text-white">{myBenefits.filter(b => !b.redeemed).length}</p>
                            <p className="text-[10px] uppercase tracking-widest text-[#52525B]">Pendientes</p>
                          </div>
                        </div>

                        {/* Benefits Grid */}
                        <div className="grid grid-cols-2 gap-4">
                          {myBenefits.map((benefit, index) => (
                            <motion.div
                              key={benefit.id}
                              initial={{ opacity: 0, y: 10 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ delay: index * 0.05 }}
                              className={`bg-[#121212] border overflow-hidden ${
                                benefit.redeemed 
                                  ? 'border-green-500/30' 
                                  : 'border-[#27272A] hover:border-[#D4AF37]/50'
                              } transition-colors`}
                            >
                              {/* Header bar */}
                              <div 
                                className="h-1"
                                style={{
                                  background: benefit.redeemed 
                                    ? 'linear-gradient(90deg, #22C55E, #16A34A)'
                                    : 'linear-gradient(90deg, #D4AF37, #F59E0B)'
                                }}
                              />
                              <div className="p-5">
                                <div className="flex gap-4">
                                  {/* Image or Icon */}
                                  {benefit.benefit_image_url ? (
                                    <img 
                                      src={benefit.benefit_image_url} 
                                      alt="" 
                                      className="w-16 h-16 object-cover flex-shrink-0"
                                    />
                                  ) : (
                                    <div className="w-16 h-16 bg-[#1A1A1A] flex items-center justify-center flex-shrink-0">
                                      <Gift size={28} className="text-[#D4AF37]" />
                                    </div>
                                  )}
                                  
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-start justify-between gap-2 mb-1">
                                      <h4 className="font-bold text-white truncate">{benefit.benefit_title}</h4>
                                      <span className={`px-2 py-0.5 text-[10px] font-bold uppercase flex-shrink-0 ${
                                        benefit.redeemed
                                          ? 'bg-green-500/20 text-green-400 border border-green-500/30'
                                          : 'bg-[#D4AF37]/20 text-[#D4AF37] border border-[#D4AF37]/30'
                                      }`}>
                                        {benefit.redeemed ? 'Canjeado' : 'Pendiente'}
                                      </span>
                                    </div>
                                    <p className="text-[#71717A] text-xs truncate mb-3">{benefit.partner_name}</p>
                                    
                                    {/* Code and date */}
                                    <div className="flex items-center justify-between gap-2">
                                      <span className="font-mono text-sm bg-[#0A0A0A] px-3 py-1.5 text-[#D4AF37] font-bold border border-[#27272A]">
                                        {benefit.code}
                                      </span>
                                      <span className="text-[10px] text-[#52525B]">
                                        {benefit.redeemed && benefit.redeemed_at
                                          ? `Canjeado ${format(new Date(benefit.redeemed_at), "d MMM yyyy", { locale: es })}`
                                          : benefit.created_at 
                                            ? `Generado ${format(new Date(benefit.created_at), "d MMM", { locale: es })}`
                                            : ''
                                        }
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </motion.div>
                          ))}
                        </div>
                      </>
                    )}
                  </motion.div>
                )}

                {/* Following Tab */}
                {activeTab === 'following' && (
                  <motion.div
                    key="following"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.3 }}
                    data-testid="following-section"
                  >
                    <div className="mb-8">
                      <h2 className="text-3xl font-black uppercase tracking-tight flex items-center gap-3">
                        <Heart className="text-[#D4AF37]" size={28} />
                        Productoras que sigo
                      </h2>
                      <p className="text-[#71717A] mt-2">
                        Seguí productoras para enterarte de sus nuevos eventos
                      </p>
                    </div>

                    {followingLoading ? (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {[1, 2, 3, 4].map((i) => (
                          <div key={i} className="bg-[#121212] border border-[#27272A] p-5 rounded-xl animate-pulse">
                            <div className="flex items-center gap-4">
                              <div className="w-16 h-16 bg-[#1A1A1A] rounded-xl" />
                              <div className="flex-1">
                                <div className="h-5 bg-[#1A1A1A] w-1/2 mb-2 rounded" />
                                <div className="h-4 bg-[#1A1A1A] w-1/3 rounded" />
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : followingProducers.length === 0 ? (
                      <div className="bg-[#121212] border border-[#27272A] p-16 rounded-xl text-center">
                        <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-[#1A1A1A] flex items-center justify-center">
                          <Heart size={36} className="text-[#3F3F46]" />
                        </div>
                        <h3 className="text-xl font-bold uppercase mb-3">No seguís ninguna productora</h3>
                        <p className="text-[#71717A] mb-8 max-w-md mx-auto">
                          Cuando sigas productoras, aparecerán aquí para que puedas ver sus próximos eventos
                        </p>
                        <Link 
                          to="/eventos" 
                          className="inline-flex items-center gap-2 bg-gradient-to-r from-[#D4AF37] to-[#F59E0B] text-black font-bold uppercase text-sm px-8 py-4 rounded-xl hover:gap-3 transition-all"
                        >
                          Explorar Eventos
                          <ChevronRight size={16} />
                        </Link>
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {followingProducers.map((producer, index) => (
                          <motion.div
                            key={producer.id}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: index * 0.05 }}
                            className="bg-[#121212] border border-[#27272A] rounded-xl overflow-hidden hover:border-[#D4AF37]/30 transition-colors"
                          >
                            <Link to={`/productora/${producer.slug}`} className="block p-5">
                              <div className="flex items-center gap-4">
                                {producer.branding?.logo_url ? (
                                  <img 
                                    src={producer.branding.logo_url} 
                                    alt={producer.business_name} 
                                    className="w-16 h-16 rounded-xl object-cover flex-shrink-0"
                                  />
                                ) : (
                                  <div className="w-16 h-16 rounded-xl bg-[#D4AF37]/10 border border-[#D4AF37]/30 flex items-center justify-center flex-shrink-0">
                                    <span className="text-2xl font-bold text-[#D4AF37]">
                                      {producer.business_name?.charAt(0)}
                                    </span>
                                  </div>
                                )}
                                
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 mb-1">
                                    <h4 className="font-bold text-white truncate text-lg">{producer.business_name}</h4>
                                    {producer.is_verified && <ShieldCheck size={16} className="text-emerald-400 flex-shrink-0" />}
                                  </div>
                                  {(producer.city || producer.province) && (
                                    <p className="text-[#71717A] text-sm truncate flex items-center gap-1">
                                      <MapPin size={12} />
                                      {[producer.city, producer.province].filter(Boolean).join(', ')}
                                    </p>
                                  )}
                                  <div className="flex items-center gap-3 mt-2">
                                    <span className="text-sm text-[#D4AF37] font-semibold">
                                      {producer.followers_count || 0} seguidores
                                    </span>
                                  </div>
                                </div>
                                
                                <ChevronRight size={20} className="text-[#3F3F46]" />
                              </div>
                            </Link>
                            
                            <div className="px-5 pb-5 flex gap-2">
                              <button
                                onClick={(e) => {
                                  e.preventDefault();
                                  handleUnfollow(producer.id);
                                }}
                                className="flex-1 py-2 px-4 bg-white/5 border border-white/10 rounded-lg text-sm text-white/60 hover:text-white hover:bg-white/10 transition-colors flex items-center justify-center gap-2"
                              >
                                <UserCheck size={16} />
                                Dejar de seguir
                              </button>
                            </div>
                          </motion.div>
                        ))}
                      </div>
                    )}
                  </motion.div>
                )}

                {/* Tickets Tab */}
                {activeTab === 'tickets' && (
                  <motion.div
                    key="tickets"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.3 }}
                    data-testid="tickets-section"
                  >
                    <div className="mb-8">
                      <h2 className="text-3xl font-black uppercase tracking-tight">Mis Entradas</h2>
                      <p className="text-[#52525B] text-sm">Tus entradas para eventos. Mostrá tu DNI en la entrada.</p>
                    </div>

                    {/* DNI Primary Info Banner */}
                    {user?.dni && tickets.length > 0 && (
                      <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="bg-gradient-to-r from-green-500/10 to-emerald-500/10 border border-green-500/30 p-4 mb-6 flex items-center gap-4"
                      >
                        <div className="w-12 h-12 bg-green-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                          <Shield size={24} className="text-green-500" />
                        </div>
                        <div className="flex-1">
                          <p className="text-green-400 font-bold text-sm uppercase tracking-wider">Tu DNI es tu entrada</p>
                          <p className="text-white text-xl font-mono font-bold">{user.dni}</p>
                          <p className="text-green-500/70 text-xs">Mostrá tu DNI físico <strong>O</strong> el QR en la entrada (son opciones alternativas)</p>
                        </div>
                      </motion.div>
                    )}

                    {ticketsLoading ? (
                      <div className="grid gap-4">
                        {[1, 2, 3].map((i) => (
                          <div key={i} className="bg-[#121212] border border-[#27272A] p-6 animate-pulse">
                            <div className="h-6 bg-[#1A1A1A] w-1/3 mb-4" />
                            <div className="h-4 bg-[#1A1A1A] w-1/2" />
                          </div>
                        ))}
                      </div>
                    ) : tickets.length === 0 ? (
                      <div className="bg-[#121212] border border-[#27272A] p-16 text-center">
                        <motion.div
                          initial={{ scale: 0.8, opacity: 0 }}
                          animate={{ scale: 1, opacity: 1 }}
                          transition={{ duration: 0.5 }}
                        >
                          <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-[#1A1A1A] flex items-center justify-center">
                            <Ticket size={36} className="text-[#3F3F46]" />
                          </div>
                          <h3 className="text-xl font-black uppercase mb-2">Sin entradas</h3>
                          <p className="text-[#A1A1AA] mb-8 max-w-sm mx-auto">
                            Aún no tienes entradas compradas. ¡Explora nuestros próximos eventos!
                          </p>
                          <Link to="/eventos" className="btn-primary inline-flex items-center gap-2">
                            Ver Eventos
                            <ChevronRight size={16} />
                          </Link>
                        </motion.div>
                      </div>
                    ) : (
                      <div className="grid gap-4">
                        {tickets.map((ticket, index) => (
                          <motion.div
                            key={ticket.id}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.3, delay: index * 0.05 }}
                            className="group overflow-hidden rounded-2xl cursor-pointer"
                            onClick={() => setSelectedTicket(ticket)}
                            data-testid={`ticket-item-${ticket.id}`}
                          >
                            {/* Card with event image background */}
                            <div className="relative h-[160px] md:h-[180px]">
                              {ticket.event_image_url ? (
                                <img
                                  src={ticket.event_image_url}
                                  alt={ticket.event_title}
                                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                                />
                              ) : (
                                <div className="absolute inset-0 bg-gradient-to-br from-zinc-800 to-zinc-900" />
                              )}
                              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-black/20" />
                              <div className="absolute inset-0 bg-gradient-to-r from-black/40 to-transparent" />

                              {/* Status + Tier badges */}
                              <div className="absolute top-3 left-3 flex gap-2">
                                <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider backdrop-blur-sm ${
                                  isTicketValid(ticket)
                                    ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                                    : 'bg-zinc-500/20 text-zinc-400 border border-zinc-500/30'
                                }`}>
                                  <span className={`w-1.5 h-1.5 rounded-full ${isTicketValid(ticket) ? 'bg-emerald-400' : 'bg-zinc-400'}`} />
                                  {isTicketValid(ticket) ? 'Valido' : 'Usado'}
                                </span>
                                {ticket.tier_name && (
                                  <span className="px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider bg-[#D4AF37]/20 text-[#D4AF37] border border-[#D4AF37]/30 backdrop-blur-sm">
                                    {ticket.tier_name}
                                  </span>
                                )}
                              </div>

                              {/* Event info overlay */}
                              <div className="absolute bottom-0 left-0 right-0 p-4">
                                <h3 className="text-lg md:text-xl font-black uppercase tracking-tight text-white mb-1 line-clamp-1">
                                  {ticket.event_title}
                                </h3>
                                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-zinc-300">
                                  {ticket.event_date && (
                                    <div className="flex items-center gap-1">
                                      <Calendar size={12} className="text-[#D4AF37]" />
                                      <span>{formatInTimeZone(new Date(ticket.event_date), 'America/Argentina/Buenos_Aires', "d MMM yyyy", { locale: es })}</span>
                                    </div>
                                  )}
                                  {(ticket.venue_name || ticket.event_location) && (
                                    <div className="flex items-center gap-1">
                                      <MapPin size={12} className="text-[#D4AF37]" />
                                      <span className="line-clamp-1">{ticket.venue_name || ticket.event_location}</span>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>

                            {/* Bottom: DNI + Ver Entrada */}
                            <div className="bg-zinc-900/95 px-4 py-3 flex items-center justify-between border-t border-dashed border-zinc-700/50">
                              {(ticket.holder_dni || ticket.buyer_dni || user?.dni) && isTicketValid(ticket) ? (
                                <div className="flex items-center gap-2">
                                  <Shield size={16} className="text-green-500" />
                                  <div>
                                    <p className="text-[10px] text-zinc-500 uppercase tracking-wider">DNI</p>
                                    <p className="text-sm font-mono font-bold text-white">{ticket.holder_dni || ticket.buyer_dni || user?.dni}</p>
                                  </div>
                                </div>
                              ) : (
                                <div className="flex items-center gap-2">
                                  <QrCode size={16} className="text-[#D4AF37]" />
                                  <span className="font-mono text-xs text-zinc-400">{ticket.ticket_code}</span>
                                </div>
                              )}
                              <Button
                                onClick={(e) => { e.stopPropagation(); setSelectedTicket(ticket); }}
                                className="btn-secondary text-xs px-3 py-1.5"
                                data-testid={`view-ticket-btn-${ticket.id}`}
                              >
                                <QrCode size={14} className="mr-1.5" />
                                Ver Entrada
                              </Button>
                            </div>
                          </motion.div>
                        ))}
                      </div>
                    )}
                  </motion.div>
                )}

                {/* Gallery Tab */}
                {activeTab === 'gallery' && (
                  <motion.div
                    key="gallery"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.3 }}
                    data-testid="gallery-section"
                  >
                    <div className="mb-8">
                      <h2 className="text-3xl font-black uppercase tracking-tight">Mis Fotos</h2>
                      <p className="text-[#52525B] text-sm">Fotos premium que has comprado</p>
                    </div>

                    {galleryLoading ? (
                      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                        {[1, 2, 3, 4].map((i) => (
                          <div key={i} className="aspect-square bg-[#121212] border border-[#27272A] animate-pulse" />
                        ))}
                      </div>
                    ) : galleryPurchases.length === 0 ? (
                      <div className="bg-[#121212] border border-[#27272A] p-16 text-center">
                        <motion.div
                          initial={{ scale: 0.8, opacity: 0 }}
                          animate={{ scale: 1, opacity: 1 }}
                          transition={{ duration: 0.5 }}
                        >
                          <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-[#1A1A1A] flex items-center justify-center">
                            <Image size={36} className="text-[#3F3F46]" />
                          </div>
                          <h3 className="text-xl font-black uppercase mb-2">Sin fotos compradas</h3>
                          <p className="text-[#A1A1AA] mb-8 max-w-sm mx-auto">
                            Compra fotos premium sin marca de agua desde nuestra galería
                          </p>
                          <Link to="/galeria" className="btn-primary inline-flex items-center gap-2">
                            Ver Galería
                            <ChevronRight size={16} />
                          </Link>
                        </motion.div>
                      </div>
                    ) : (
                      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                        {galleryPurchases.map((purchase, index) => (
                          <motion.div
                            key={purchase.id}
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 0.3, delay: index * 0.05 }}
                            whileHover={{ y: -4, borderColor: '#fff' }}
                            className="bg-[#121212] border border-[#27272A] overflow-hidden group transition-all duration-300"
                            data-testid={`gallery-purchase-${purchase.id}`}
                          >
                            <div className="aspect-square relative overflow-hidden">
                              {purchase.thumbnail_url ? (
                                <img
                                  src={purchase.thumbnail_url}
                                  alt="Foto adquirida"
                                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                                />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center bg-[#1A1A1A]">
                                  <Image size={32} className="text-[#52525B]" />
                                </div>
                              )}
                              
                              {/* Download type badge */}
                              <div className={`absolute top-2 left-2 px-2 py-1 text-[10px] font-mono uppercase ${
                                purchase.download_type === 'premium'
                                  ? 'bg-yellow-500/20 text-yellow-500 border border-yellow-500/30'
                                  : 'bg-green-500/20 text-green-500 border border-green-500/30'
                              }`}>
                                {purchase.download_type === 'premium' ? 'Premium' : 'Gratis'}
                              </div>
                              
                              <div className="absolute top-2 right-2">
                                {purchase.download_type === 'premium' ? (
                                  <Crown size={16} className="text-yellow-500" />
                                ) : (
                                  <Gift size={16} className="text-green-500" />
                                )}
                              </div>
                            </div>
                            
                            <div className="p-4">
                              <p className="text-xs text-[#A1A1AA] mb-3 truncate">
                                {purchase.file_name || `Imagen ${purchase.file_id?.slice(0, 8) || 'HD'}`}
                              </p>
                              <Button
                                onClick={() => handleDownloadPurchase(purchase)}
                                disabled={purchase.status !== 'completed' || downloadingId === purchase.id}
                                className={`w-full text-xs ${
                                  purchase.status === 'completed'
                                    ? 'btn-primary'
                                    : 'bg-[#27272A] text-[#52525B] cursor-not-allowed'
                                }`}
                              >
                                {downloadingId === purchase.id ? (
                                  <Loader2 size={14} className="animate-spin" />
                                ) : (
                                  <>
                                    <Download size={14} className="mr-2" />
                                    Descargar HD
                                  </>
                                )}
                              </Button>
                              <p className="text-[10px] text-[#52525B] mt-2 text-center">
                                {format(new Date(purchase.created_at), "d MMM yyyy", { locale: es })}
                              </p>
                            </div>
                          </motion.div>
                        ))}
                      </div>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Ticket Modal - Premium Design with Event Image */}
      <Dialog open={!!selectedTicket} onOpenChange={() => setSelectedTicket(null)}>
        <DialogContent className="bg-transparent border-0 w-[95vw] max-w-sm p-0 overflow-hidden max-h-[90vh]">
          {selectedTicket && (
            <div className="relative">
              {/* Premium Ticket Card */}
              <div className="relative overflow-hidden rounded-3xl">
                {/* Golden border glow effect */}
                <div className="absolute inset-0 rounded-3xl bg-gradient-to-b from-[#D4AF37]/30 via-transparent to-[#D4AF37]/20 p-[1px]">
                  <div className="absolute inset-0 rounded-3xl bg-[#0A0A0A]" />
                </div>

                <div className="relative bg-gradient-to-b from-[#121212] to-[#0A0A0A] rounded-3xl border border-[#D4AF37]/30">
                  {/* Top: Event image with premium overlay */}
                  <div className="relative h-44 overflow-hidden rounded-t-3xl">
                    {selectedTicket.event_image_url ? (
                      <img
                        src={selectedTicket.event_image_url}
                        alt={selectedTicket.event_title}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-zinc-800 via-zinc-900 to-black" />
                    )}
                    {/* Multi-layer gradient overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-black/60 to-transparent" />
                    <div className="absolute inset-0 bg-gradient-to-r from-black/30 to-transparent" />
                    
                    {/* Decorative corner accent */}
                    <div className="absolute top-0 right-0 w-24 h-24">
                      <div className="absolute top-2 right-2 w-16 h-16 border-t-2 border-r-2 border-[#D4AF37]/30 rounded-tr-2xl" />
                    </div>

                    {/* Status badge */}
                    <div className="absolute top-3 left-3">
                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider backdrop-blur-md shadow-lg ${
                        isTicketValid(selectedTicket)
                          ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/40'
                          : 'bg-zinc-500/20 text-zinc-400 border border-zinc-500/40'
                      }`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${isTicketValid(selectedTicket) ? 'bg-emerald-400 animate-pulse' : 'bg-zinc-400'}`} />
                        {isTicketValid(selectedTicket) ? 'Válido' : 'Usada'}
                      </span>
                    </div>

                    {/* Event title */}
                    <div className="absolute bottom-3 left-4 right-4">
                      <p className="text-[#D4AF37] text-[9px] font-bold uppercase tracking-[0.15em] mb-0.5">Entrada para</p>
                      <h3 className="text-lg font-black uppercase tracking-tight text-white leading-tight drop-shadow-lg line-clamp-2">
                        {selectedTicket.event_title}
                      </h3>
                    </div>
                  </div>

                  {/* Content Section */}
                  <div className="p-4">
                    {selectedTicket.status === 'used' ? (
                      /* Ticket ya usado */
                      <div className="bg-zinc-800/50 border border-zinc-700/50 rounded-xl p-4 text-center">
                        <div className="w-10 h-10 bg-zinc-700/50 rounded-full flex items-center justify-center mx-auto mb-2">
                          <CheckCircle size={20} className="text-zinc-400" />
                        </div>
                        <p className="text-zinc-300 text-sm font-bold uppercase tracking-wider mb-1">
                          Entrada utilizada
                        </p>
                        {selectedTicket.checked_in_at && (
                          <p className="text-[10px] text-zinc-500">
                            {format(new Date(selectedTicket.checked_in_at), "d MMM 'a las' HH:mm", { locale: es })}
                          </p>
                        )}
                      </div>
                    ) : (
                      /* Ticket válido */
                      <>
                        {/* DNI Option */}
                        <div className="bg-gradient-to-br from-emerald-500/10 to-green-500/10 border border-emerald-500/30 rounded-xl p-4 text-center mb-3">
                          <div className="w-8 h-8 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-2">
                            <Shield size={16} className="text-emerald-400" />
                          </div>
                          <p className="text-emerald-400 text-[10px] font-bold uppercase tracking-wider mb-1">
                            Opción 1: DNI Físico
                          </p>
                          <p className="text-2xl font-black font-mono text-white">
                            {selectedTicket.holder_dni || selectedTicket.buyer_dni || user?.dni || 'N/A'}
                          </p>
                          <p className="text-emerald-500/60 text-[10px] mt-1">
                            Mostrá tu DNI en la entrada
                          </p>
                        </div>

                        {/* Separator */}
                        <div className="flex items-center gap-3 mb-3">
                          <div className="flex-1 h-px bg-[#27272A]" />
                          <span className="text-[#52525B] text-[9px] uppercase tracking-wider">o bien</span>
                          <div className="flex-1 h-px bg-[#27272A]" />
                        </div>

                        {/* QR Code */}
                        <div className="text-center">
                          <p className="text-[#71717A] text-[9px] uppercase tracking-wider mb-2">
                            Opción 2: Código QR
                          </p>
                          <div className="relative p-0.5 rounded-xl bg-gradient-to-br from-[#D4AF37]/30 via-[#D4AF37]/10 to-[#D4AF37]/30 inline-block shadow-[0_0_20px_rgba(212,175,55,0.1)]">
                            <div className="bg-white p-3 rounded-lg">
                              <QRCodeSVG
                                value={selectedTicket.qr_data || `BRAVO|${selectedTicket.holder_dni || selectedTicket.buyer_dni || ''}|${selectedTicket.ticket_code}|${selectedTicket.event_id}`}
                                size={120}
                                level="H"
                                bgColor="#ffffff"
                                fgColor="#0A0A0A"
                              />
                            </div>
                          </div>
                          
                          {/* Ticket Code */}
                          <div className="mt-3">
                            <p className="text-[9px] text-zinc-500 uppercase tracking-wider mb-0.5">Código</p>
                            <p className="font-mono text-sm font-bold tracking-wider text-white">
                              {selectedTicket.ticket_code}
                            </p>
                          </div>
                        </div>
                      </>
                    )}

                    {/* Event Details - Compact */}
                    <div className="mt-4 pt-3 border-t border-[#27272A]/50">
                      <div className="flex flex-wrap justify-center gap-4 text-[11px] text-[#71717A]">
                        {selectedTicket.event_date && (
                          <div className="flex items-center gap-1.5">
                            <Calendar size={12} className="text-[#D4AF37]" />
                            <span>{formatInTimeZone(new Date(selectedTicket.event_date), 'America/Argentina/Buenos_Aires', "d MMM yyyy", { locale: es })}</span>
                          </div>
                        )}
                        {(selectedTicket.venue_name || selectedTicket.event_location) && (
                          <div className="flex items-center gap-1.5">
                            <MapPin size={12} className="text-[#D4AF37]" />
                            <span className="truncate max-w-[120px]">{selectedTicket.venue_name || selectedTicket.event_location}</span>
                          </div>
                        )}
                      </div>
                      {selectedTicket.tier_name && (
                        <div className="mt-2 text-center">
                          <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#D4AF37]/10 text-[#D4AF37] border border-[#D4AF37]/20">
                            {selectedTicket.tier_name}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Cancel Subscription Modal */}
      <Dialog open={showCancelModal} onOpenChange={setShowCancelModal}>
        <DialogContent className="bg-[#121212] border border-[#27272A] max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-black uppercase tracking-tight flex items-center gap-2">
              <Crown className="text-[#D4AF37]" />
              Cancelar Suscripción
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-4">
            <p className="text-[#A1A1AA] mb-4">
              ¿Estás seguro que querés cancelar tu suscripción FULL BRAVX?
            </p>
            
            <div className="bg-yellow-500/10 border border-yellow-500/30 p-3 mb-4 rounded-lg">
              <p className="text-yellow-400 text-sm font-medium">
                Tu acceso continuará hasta el final del período actual.
              </p>
              <p className="text-yellow-400/70 text-xs mt-1">
                Podés reactivar tu suscripción en cualquier momento antes de que expire.
              </p>
            </div>
            
            <div className="bg-[#1A1A1A] border border-[#27272A] p-4 mb-4 rounded-lg">
              <p className="text-white font-medium mb-2">Vas a perder acceso a:</p>
              <ul className="text-[#A1A1AA] text-sm space-y-1">
                <li>• 5 descargas HD gratis por mes</li>
                <li>• Auras premium exclusivas</li>
                <li>• Descuentos en cargo de servicio</li>
                <li>• Acceso prioritario a eventos</li>
                <li>• Beneficios en partners</li>
              </ul>
            </div>
            
            {subscriptionData?.status === 'trial' && (
              <div className="bg-green-500/10 border border-green-500/30 p-3 mb-4 rounded-lg">
                <p className="text-green-400 text-sm">
                  <Gift className="inline mr-1" size={14} />
                  Estás en período de prueba. No se te cobrará nada si cancelás ahora.
                </p>
              </div>
            )}
            
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => setShowCancelModal(false)}
                className="flex-1 border-[#27272A]"
              >
                Mantener Suscripción
              </Button>
              <Button
                onClick={handleCancelSubscription}
                disabled={cancellingSubscription}
                className="flex-1 bg-red-500 hover:bg-red-600 text-white"
                data-testid="confirm-cancel-btn"
              >
                {cancellingSubscription ? (
                  <Loader2 className="animate-spin mr-2" size={16} />
                ) : null}
                Sí, Cancelar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
};

export default UserAccountPage;
